import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { resolveExposureBandLabel } from 'src/app/shared/utils/risk-level-parsing.util';
import {
    isExcludedFromStatistics,
    resolveDistributionLabelKey,
    resolveDistributionUsedKey,
} from 'src/app/shared/utils/distribution-label.util';
import {
  afterNextRender,
  Component,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy
} from "@angular/core";
import type * as am5 from "@amcharts/amcharts5";
import type * as am5xy from "@amcharts/amcharts5/xy";
import * as Sentry from "@sentry/angular";
import { RiskRegisterService } from "src/app/services/risk-register.service";
import { RiskAssessmentService } from "src/app/services/risk-assessment.service";
import {
    AssessmentMethodEnum,
    PdfPlot,
    QqPlot,
    RiskRegister,
    RiskRegisterSample,
} from "src/app/models/risk-assessment.model";
import {
    AssessmentStatusEnum,
    RiskRegisterAssessmentIconsEnum,
    RiskRegisterAssessmentStatusEnum,
} from "src/app/enums/assessmentStatus.enum";
import {
    ExceedanceProbabilityExposureEnum,
    FrequencyExposureEnum,
    QualitativeMetrics,
} from "src/app/shared/constants/qualitativeMetric.constants";
import { getEnumList, getNamedEnum } from "src/app/_helpers/enum-helper";
import { CommonService } from "src/app/services/common.service";
import { ToastService } from "src/app/services/toast.service";
import { ValueConversionService } from "src/app/services/value-conversion.service";
import { MatDialog } from "@angular/material/dialog";
import { SampleGridViewDialogComponent } from "../sample-grid-view-dialog/sample-grid-view-dialog.component";
import { DateFormatService } from "src/app/services/dateFormat.service";
import { ParameterService } from "src/app/services/parameter.service";
import { Parameter } from "src/app/models/parameter.model";
import { HeaderFilteringBaseComponent } from "src/app/shared/header-filtering-base/header-filtering-base.component";
import { ExposureRiskEnum } from "src/app/enums/exposureRisk.enum";
import { OelSections } from "src/app/enums/oelSections.enum";
import { NonDetectExclusionEffect } from "src/app/enums/nonDetectExclusionEffect.enum";
import { DistributionPlotUnavailableReason } from "src/app/enums/distributionPlotUnavailableReason.enum";
import { getRiskColor, getRiskLevelName } from "src/app/_helpers/risk-color.helper";
import { ControlService } from "src/app/services/control.service";
import { ChartService } from "src/app/services/chart.service";
import { OperatorTypeEnum } from "src/app/enums/operatorType.enum";
import { RiskRegisterControlLinkType } from "src/app/enums/riskRegisterControlLinkType.enum";
import { Control } from "src/app/models/control.model";
import { MatTableDataSource } from "@angular/material/table";
import nameOf from "src/app/shared/form/nameOf.helper";
import { isApprovedRiskRegisterStatus } from "src/app/shared/utils/risk-register-status.util";
import { ControlStatusEnum } from "src/app/enums/control-status.enum";
import { ControlTypeEnum } from "src/app/enums/control-type.enum";
import { MatTabChangeEvent } from "@angular/material/tabs";
import { ILookup } from "src/app/shared/form/lookup.model";
import {
    FormField,
    MultiSelectionFormField,
} from "src/app/shared/form/formField.model";
import { ControlsListComponent } from "src/app/components/controls-list/controls-list.component";
import { ColumnSpan } from "src/app/shared/form/columnSpan.model";
import { LinkControlDialogComponent } from "../link-control-dialog/link-control-dialog.component";
import { ControlOrganizationLevelSegService } from "src/app/services/controlOrganizationLevelSeg.service";
import { catchError, EMPTY, forkJoin, of, Subject, switchMap } from "rxjs";
import { takeUntil, tap } from "rxjs/operators";
import { SampleOutlierService } from "src/app/services/sample-outlier.service";
import { PermissionService } from "src/app/services/permission.service";
import { RolePermissionObjects } from "src/app/shared/constants/rolePermissionObjects.constants";
import { GridPermissions, gridToPermissionFlags } from "src/app/shared/grid/core-grid/gridPermissions.enum";
import { ControlOrganizationLevelSeg } from "src/app/models/controlOrganizationLevelSeg.model";
import { dialogExcludeSelectors } from "src/app/_helpers/click-outside.directive";
import { TranslateService } from "src/app/services/translate.service";
import { UnitSymbols, Units } from "src/app/enums/units.enum";
import { getOelCorrectionMethodNames } from "src/app/enums/oelCorrectionMethod.enum";
import { JurisdictionRiskConfigService } from "src/app/services/Jurisdiction-risk-config.service";
import { JurisdictionRiskConfig } from "src/app/models/jurisdictionRiskConfig.model";
import {
    QualitativeConfig,
    QualitativeConfigOption,
    LikelihoodMapping,
    QualitativeConfigApi,
    ConsequenceRating,
} from "src/app/models/jurisdictionRiskConfig.model";
import { parseConsequenceRatings } from "src/app/shared/utils/consequence-ratings.util";
import { environment } from "src/environments/environment";
import { SampleCensorSign, SampleCensorType } from "src/app/enums/sampleCensorType.enum";
import {
    CensoredDataMethod,
    CENSORED_DATA_METHOD_DISPLAY_LABELS,
} from "src/app/enums/censoredDataMethod.enum";
import { QuantitativeDecisionStatisticNamesEnum } from "src/app/enums/quantitativeDecisionStatistic.enum";
import {
    ComplianceStateDisplay,
    ComplianceStateDisplayMap,
    ComplianceStateEnum,
} from "src/app/enums/complianceState.enum";
import { ComplianceBasisEnum } from "src/app/enums/complianceBasis.enum";
import {
    BayesianAnalysisRunResponse,
    BayesianAnalysisStoredResultResponse,
} from "src/app/models/bayesian-analysis.model";
import { BayesianConfig } from "src/app/models/bayesian-config.model";
import { FileAttachment } from "src/app/models/fileAttachment.model";
import { RiskRegisterAttachmentRow } from "src/app/models/risk-register-attachment.model";
import { Lookup } from "src/app/models/lookup.model";
import { FileTypeEnum } from "src/app/enums/fileType.enum";
import { AssetTypeEnum } from "src/app/enums/assetType.enum";
import { UserService } from "src/app/services/user.service";
import { FileAttachmentService } from "src/app/services/file-attachment.service";
import {
    AttachmentsDialogComponent,
    IAttachmentsFormDialogData,
} from "../attachments-dialog/attachments-dialog.component";
import { DeleteDialog } from "src/app/shared/delete-dialog/delete-dialog.component";
import { CreatePayload } from "src/app/shared/grid/crud.service";
import { openFileInNewTab } from "src/app/shared/utils/file-link.util";
import { formatFileSizeInMB } from "src/app/shared/utils/file-size.util";
import {
    RISK_REGISTER_ATTACHMENT_FILE_TYPES,
    RISK_REGISTER_ATTACHMENT_MAX_FILE_SIZE_MB,
    RISK_REGISTER_MAX_ATTACHMENTS,
} from "src/app/shared/constants/risk-register-attachments.constants";

interface ExposureLikelihoodLevelsStorage {
    ConsequenceRatings?: ConsequenceRating[];
    [key: string]: unknown;
}

interface ExposureLikelihoodLevelConfig {
    Name?: string;
    QuantitativeName?: string;
    QualThresholds?: [number, number];
    QuantThreshold_OEL?: [number, number];
    Description?: string;
    Color?: string;
}

interface BayesianDistributionItem {
    category: string;
    probability: number;
    color: string;
    score: number;
}

interface BayesianSampleRow {
    sampleId: string;
    sampleDate: Date | null;
    sampleResult: string;
    censoring: string;
}

interface BayesianPanelRow {
    rating: string;
    value: number;
    color: string;
}

type BayesianPanelKey =
    | "uninformativePrior"
    | "uninformativeLikelihood"
    | "uninformativePosterior"
    | "informativePrior"
    | "informativeLikelihood"
    | "informativePosterior";

// Below this likelihood mass the Bayes back-out (prior ∝ posterior / likelihood) is
// numerically unstable, so that bin contributes no informative-prior mass.
const BAYESIAN_LIKELIHOOD_EPSILON = 1e-6;

// amCharts derives its toFixed() decimal count from a value's exponent, so a
// near-zero probability (e.g. 4.15e-94 from a posterior far above the OEL) makes it
// call Number.toFixed with >100 digits and throw a RangeError, blanking every panel.
// Rounding chart-fed values to 6 decimals collapses anything below 5e-7 to exactly 0
// so the formatter only ever receives 0 or a value of at least 1e-6.
const BAYESIAN_CHART_VALUE_DECIMALS = 6;

// The three visually distinct states a plotted sample point can carry. Driven by
// sampleCensorType and the register's CensoredDataMethod (encoded upstream as a
// null substitutedValue for excluded non-detects). Rendered marker-first so the
// three remain distinguishable in greyscale and for colour-blind users:
//   detected    — filled circle, series colour.
//   substituted — open circle + downward tick, muted (--neutral-500).
//   excluded    — open circle + downward tick, greyed (--neutral-400); contributed to no line and is not in N.
type SampleChartPointState = "detected" | "substituted" | "excluded";

// A RiskRegisterSample prepared for the sample chart: startDate is epoch-
// MILLISECONDS (converted from the model's epoch-seconds), and is always
// present because dateless rows are filtered out during preparation. plottedValue
// is the value drawn on the y-axis (the substituted value, so points and the
// reference lines share one basis); reportedDisplay/substitutedDisplay and
// pointState feed the tooltip and per-point bullet styling.
type SampleChartRow = Omit<RiskRegisterSample, "startDate"> & {
    startDate: number;
    plottedValue: number;
    pointState: SampleChartPointState;
    reportedDisplay: string;
    substitutedDisplay: string;
    actionHint?: string;
};

type QqPointDetails = {
    sampleNumber: string;
    date: string;
    reported: string;
    nonDetect: boolean;
    markable: boolean;
};

// amCharts treats [ and { in label text as formatting and data placeholders; double them so sample data shows literally.
function escapeChartText(text: string): string {
    return text.replace(/\[/g, "[[").replace(/\{/g, "{{");
}

// One Q-Q point with the sample it came from. sampleId is set only when that sample can be marked as an outlier
// from the chart; the tooltip and accessible label are built per point because the non-detect and action lines
// only apply to some points.
export type QqChartRow = {
    theoretical: number | null;
    empirical: number | null;
    sampleId: string | null;
    tooltip: string;
    ariaLabel: string;
};

interface BayesianMetricRow {
    label: string;
    tooltip: string;
    estimate: string;
    median: string;
    credibleInterval: string;
    ucl70: string;
    ucl90: string;
    emphasis: boolean;
}

// Plain-language explanations (i18n keys) for the statistical parameters, surfaced via
// info icons for non-specialist users.
const BAYESIAN_STAT_TOOLTIPS: Record<string, string> = {
    geometricMean:
        "Geometric mean: the typical exposure level. Exposure data are skewed, so the geometric mean represents the centre of the readings better than a plain average.",
    geometricStandardDeviation:
        "Geometric standard deviation: how much the exposures vary. A value of 1 means every reading is identical; higher values mean the readings are more spread out.",
    percentile95:
        "95th percentile: the level 95% of exposures stay below — a reasonable worst case. About 1 in 20 exposures is expected to be higher.",
    exceedanceFraction:
        "Exceedance fraction: the estimated share of all exposures that go above the OEL (occupational exposure limit).",
};

@Component({
  standalone: false,
    selector: "app-risk-register-details-popup",
    templateUrl: "./risk-register-details-popup.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./risk-register-details-popup.component.scss",
})
export class RiskRegisterDetailsPopupComponent
    extends HeaderFilteringBaseComponent
    implements OnInit, OnChanges, OnDestroy
{
    @Input() show: boolean = true;
    @Input() id: string = '';
    @Input() riskLevels = [];
    @Input() jurisdictionId: string | null = null;
    @Input() parentAssessmentStatus: AssessmentStatusEnum | null = null;
    isParentAssessmentApproved = false;
    canRecalculate = false;
    @Output() hideRiskDetailEmit = new EventEmitter<string>();
    @Output() updateGridEmit = new EventEmitter<boolean>();
    selectedRiskTab: number = 0;
    excludeSelectors = [
        ...dialogExcludeSelectors,
        ".analyte-cell",
        ".sample-selector-dialog",
    ].join(",");

    readonly CHART_DIV_ID = "risk-register-details-chart";
    // A DateAxis defaults to a one-day baseInterval and aggregates every item that
    // falls in the same interval into a single point, so samples collected on one
    // day (a SEG sampled across one shift) collapse. A millisecond baseInterval
    // keeps each sample as its own point; grid labelling stays date-based via the
    // axis's default gridIntervals.
    static readonly SAMPLE_CHART_BASE_INTERVAL = {
        timeUnit: "millisecond",
        count: 1,
    } as const;
    // Padding applied above and below the plotted range so bullets are never
    // clipped at the axis edge, expressed as a fraction of the full value span.
    static readonly SAMPLE_CHART_AXIS_PAD_RATIO = 0.05;
    // Absolute floor for the zero-span axis pad. Only reached when every plotted
    // value is exactly zero (so the proportional pad is also zero); a tiny epsilon
    // just keeps min < max. For any non-zero value the pad stays proportional, so
    // the axis scales with the data unit (ug/m3, ppb, dB, dose%, ...) rather than
    // assuming a unit scale.
    static readonly SAMPLE_CHART_MIN_AXIS_PAD = Number.EPSILON;
    // Marker colours for the sample-chart point states. Detected uses the brand
    // primary; non-detects stay MUTED (neutral greys) so they never compete with
    // the colour-semantic OEL / geometric-mean / decision-statistic lines. Values
    // mirror the design tokens; readCssColor resolves the live token at runtime and
    // falls back to these when the custom property is unavailable (e.g. under tests).
    static readonly SAMPLE_POINT_DETECTED_COLOR = "#25245C"; // --primary
    static readonly SAMPLE_POINT_SUBSTITUTED_COLOR = "#6B7280"; // --neutral-500
    static readonly SAMPLE_POINT_EXCLUDED_COLOR = "#9CA3AF"; // --neutral-400
    // Marker-state key rendered under the sample chart; three marker styles with no
    // legend is a puzzle. Shown only while the Sample Results chart is active.
    showSampleMarkerLegend = false;
    readonly sampleMarkerLegend: Array<{ state: SampleChartPointState; label: string }> = [
        { state: "detected", label: "Detected" },
        { state: "substituted", label: "Non-detect (substituted)" },
        { state: "excluded", label: "Non-detect (excluded)" },
    ];

    // A tick marks a censored observation — the true value lies at or below the
    // point. Both non-detect states are censored, so both carry it; only detected
    // points omit it. Single source of truth for the legend and the chart bullet.
    sampleMarkerHasTick(state: SampleChartPointState): boolean {
        return state !== "detected";
    }
    chart: am5xy.XYChart;
    private chartRoot: am5.Root | null = null;
    loading = false;
    riskDetails: RiskRegister;
    initialRiskDetails: RiskRegister;
    riskRegisterAssessmentStatusEnum = getNamedEnum(
        RiskRegisterAssessmentStatusEnum,
    );
    exceedanceProbabilityList: QualitativeConfigOption[] = [];
    frequencyExposureList: QualitativeConfigOption[] = [];
    exceedanceProbabilityExposureEnum = ExceedanceProbabilityExposureEnum;
    frequencyExposureEnum = FrequencyExposureEnum;
    qualitativeLikelihoodText = "";
    quantitativeLikelihoodText = "";
    // DASH-2808: the Exposure Band (Quantitative Name) shown on its own row above the
    // quantitative Likelihood Rating, plus the band's % of OEL range in brackets.
    exposureBandText = "";
    exposureBandRange = "";
    exposureLabelValue = "";
    /** Colour of the resolved likelihood level for the header Exposure Band chip; empty when no band resolves. */
    exposureLabelColor = "";
    /** % OEL range shown in the HEADER only. Empty for qualitative registers (no % of OEL result). */
    headerBandRange = "";
    /** Id of the info popover currently pinned open by a click; null when none is pinned. */
    pinnedInfoPopover: string | null = null;
    riskStatus: "Draft" | "Approved" = "Draft";
    assessmentMethodEnum = AssessmentMethodEnum;
    methodOfAssessmentToggleValue = false;
    isQualitativeExpanded = false;
    riskRating = 0;
    riskRatingColor: string;
    riskRatingName: string | null = null;
    startDate: Date = new Date();
    endDate: Date = new Date();
    editDateRange: boolean;
    isInteractionPaused: boolean;
    parameter: Parameter;
    filteredOelSections: string[] = [];
    isChartLoading: boolean;
    selectedTabIndex: number;
    exposureRisk = getNamedEnum(ExposureRiskEnum);
    riskResidualRating = 0;
    riskResidualRatingColor: string;
    residualLikelihoodText: string;
    controls: (ILookup | Control)[] = [];
    controlsData: Control[];
    dataSource = new MatTableDataSource<Control>();
    displayedColumns = [];
    controlStatusEnum = ControlStatusEnum;
    controlTypeEnum = ControlTypeEnum;
    controlAssessmentField: FormField;
    attachmentsDataSource = new MatTableDataSource<RiskRegisterAttachmentRow>();
    readonly attachmentDisplayedColumns = [
        nameOf<RiskRegisterAttachmentRow>((x) => x.name),
        nameOf<RiskRegisterAttachmentRow>((x) => x.description),
        nameOf<RiskRegisterAttachmentRow>((x) => x.typeLabel),
        nameOf<RiskRegisterAttachmentRow>((x) => x.sizeLabel),
        nameOf<RiskRegisterAttachmentRow>((x) => x.uploadedByName),
        nameOf<RiskRegisterAttachmentRow>((x) => x.dateCreated),
        "actions",
    ];
    attachmentsLoading = false;
    attachmentsLocked = false;
    canAddAttachment = false;
    private attachmentCount = 0;
    controlTreatmentField: FormField;
    oelCorrectionExpanded = false;
    oelCorrectionMethodEnum = getOelCorrectionMethodNames();
    UnitSymbols = UnitSymbols;
    qqFitSlope: number | null = null;
    qqFitR2: number | null = null;
    // G19/DASH-2977 (FE-3, AC6): when no distribution plot can be drawn the chart div used to
    // collapse silently (a known defect). Precomputed empty-state copy that names the cause,
    // rendered in place of the collapsed chart. Empty string => a plot is available.
    plotEmptyStateMessage = '';
    originalConsequenceRating: number | null = null;
    originalConsequenceRatingComment: string | null = null;
    consequenceRatingChanged: boolean = false;
    hasConsequenceRatingComment = false;
    isConsequenceRatingCommentExpanded = false;
    qualitativeConfig: QualitativeConfig | undefined;
    likelihoodMapping: LikelihoodMapping[] = [];
    consequenceRatings: ConsequenceRating[] = [];
    matrixRows: Array<{ score: number; classification: string }> = [];
    matrixColumns: ConsequenceRating[] = [];
    matrixCells: Array<Array<{ color: string; isActive: boolean; tooltip: string }>> = [];
    private likelihoodFactorsByRange = new Map<string, string>();
    // DASH-2278: the configured likelihood levels read straight from the
    // exposureLikelihoodLevels config (the same "likelihood table" the jurisdiction
    // config form edits). This is the single source of truth for the likelihood
    // info-icon table — each entry carries the level's score (numeric key), Name and
    // Description exactly as configured, so no fragile join back through the
    // qualitative likelihoodMapping (which doesn't carry descriptions) is needed.
    private likelihoodLevels: Array<{
        score: number;
        name: string;
        description: string;
        quantitativeName?: string;
        minQuantThreshold?: number | null;
        maxQuantThreshold?: number | null;
    }> = [];

    // Pre-computed view of likelihoodLevels merged with mapping-sourced descriptions.
    // Refreshed in initializeQualitativeOptions (after likelihoodMapping is populated)
    // so the template binds a plain field instead of invoking a getter every CD cycle.
    likelihoodLevelDescriptionsCache: Array<{ score: number; name: string; description: string }> = [];
    private likelihoodLevelColors = new Map<number, string>();
    private readonly destroy$ = new Subject<void>();
    // Chart tab to reopen after the next reload, so marking an outlier from a chart keeps the user on that chart.
    private chartTabToRestore: string | null = null;

    readonly bayesianPanelIds: Record<BayesianPanelKey, string> = {
        uninformativePrior: "risk-bayesian-uninformative-prior",
        uninformativeLikelihood: "risk-bayesian-uninformative-likelihood",
        uninformativePosterior: "risk-bayesian-uninformative-posterior",
        informativePrior: "risk-bayesian-informative-prior",
        informativeLikelihood: "risk-bayesian-informative-likelihood",
        informativePosterior: "risk-bayesian-informative-posterior",
    };
    private bayesianRoots: am5.Root[] = [];
    bayesianEnabled = false;
    bayesianFrameworkLabel = "-";
    bayesianDecisionStatisticLabel = "-";
    complianceDisplay: ComplianceStateDisplay | null = null;
    decisionNarrative = "";
    bayesianDefaultPriorIsInformative = false;
    bayesianUseInformativePrior = false;
    // Set once the user toggles the prior this session so an async hydrate or a
    // completed run doesn't clobber their selection back to the jurisdiction default.
    private bayesianPriorSelectionDirty = false;
    bayesianPriorOverrideAuditNote = "";
    bayesianPriorPayloadKey = "bayesianUseInformativePrior";
    bayesianDistributionUninformative: BayesianDistributionItem[] = [];
    bayesianDistributionInformative: BayesianDistributionItem[] = [];
    bayesianDecisionValue: number | null = null;
    bayesianConvertedDecisionValue = "-";
    bayesianDecisionState: "above" | "below" | "unknown" = "unknown";
    bayesianDecisionStateLabel = "Unknown";
    bayesianOelMissing = false;
    bayesianNoOelReason = "";
    bayesianSamplesMissing = false;
    bayesianCensoringGuardrail = false;
    bayesianInsufficientSamples = false;
    bayesianNoQraFallback = false;
    bayesianResultStale = false;
    bayesianRunLoading = false;
    bayesianRunButtonLabel = "Run Bayesian Analysis";
    // Informative (QRA) prior temporarily disabled — the tab renders uninformative only.
    readonly bayesianInformativeDisabled = true;
    // Full posterior stat table (GM/GSD/P95/exceedance × estimate/median/CrI/UCL) for the
    // uninformative prior.
    bayesianUninformativeMetrics: BayesianMetricRow[] = [];
    // Colour → exposure-band legend for the distribution charts (bars are coloured by band).
    bayesianBandLegend: Array<{ color: string; label: string }> = [];
    // Plain-language interpretation of the result shown in the decision panel.
    bayesianResultExplanation: { title: string; body: string } | null = null;
    bayesianSamplesUsed: BayesianSampleRow[] = [];
    bayesianJurisdictionConfig: BayesianConfig = {};
    quantitativeDecisionStatisticNames = getNamedEnum(
        QuantitativeDecisionStatisticNamesEnum,
    );

    // Pre-computed view of the configured Exposure Likelihood Levels for the
    // Quantitative Likelihood Rating info-icon table — one row per level with its
    // Likelihood Value Factor (score), Quantitative Name and Quant % OEL range, sourced
    // straight from the jurisdiction's Risk Configuration (exposureLikelihoodLevels).
    quantitativeLikelihoodLevelsCache: Array<{ factor: number; quantitativeName: string; quantOelRange: string }> = [];

    constructor(
        private riskRegisterService: RiskRegisterService,
        private riskAssessmentService: RiskAssessmentService,
        protected commonService: CommonService,
        private toastService: ToastService,
        private valueConversionService: ValueConversionService,
        private dialog: MatDialog,
        private dateFormatService: DateFormatService,
        private parameterService: ParameterService,
        private controlService: ControlService,
        private controlOrgLevelSegService: ControlOrganizationLevelSegService,
        private translateService: TranslateService,
        private jurisdictionRiskConfigService: JurisdictionRiskConfigService,
        private chartService: ChartService,
        private injector: Injector,
        private userService: UserService,
        private fileAttachmentService: FileAttachmentService,
        private sampleOutlierService: SampleOutlierService,
        private permissionService: PermissionService,
    ) {
        super(commonService);
        this.displayedColumns = [
            nameOf<Control>((x) => x.controlHierarchyBucketName),
            nameOf<Control>((x) => x.name),
            nameOf<Control>((x) => x.controlType),
            "segOrgStatus",
            nameOf<ControlOrganizationLevelSeg>(
                (x) => x.targetImplementationEnd,
            ),
            nameOf<Control>((x) => x.ownerName),
        ];
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.controlService.getAll().subscribe({
            next: (res) => {
                this.controls = res;
                this.setControlFields();
            },
            error: (error) => {
                this.toastService.showError(error);
            },
        });
    }

    runBayesianAnalysisInPlace(): void {
        if (!this.id || this.bayesianRunLoading) {
            return;
        }

        this.bayesianRunLoading = true;
        this.updateBayesianRunButtonLabel();

        this.riskRegisterService
            .runBayesianAnalysis(
                this.id,
                this.bayesianUseInformativePrior && !this.bayesianInformativeDisabled ? 1 : 0,
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.applyBayesianRunResult(response);
                    this.bayesianRunLoading = false;
                    this.updateBayesianRunButtonLabel();
                    this.toastService.showSuccess(
                        "Bayesian analysis completed successfully",
                        true,
                    );
                },
                error: (error) => {
                    this.bayesianRunLoading = false;
                    this.updateBayesianRunButtonLabel();
                    this.toastService.showError(error);
                },
            });
    }

    private applyBayesianRunResult(result: BayesianAnalysisRunResponse): void {
        // DASH-2581: the Run endpoint returns both prior distributions (each with its
        // own categoryProbabilities + GM/GSD) plus hasInformativePrior. Persist them in
        // the same nested shape the stored-result hydrate path uses so a single reader
        // (prepareBayesianViewModel) drives the view for both run and hydrate.
        const priorMode = result.priorMode;
        const hasInformativePrior = result.hasInformativePrior === true;
        const decisionValue = result.decisionValue;
        const noOelReason = result.noOelReason || "";
        const hasOel = result.hasOel;
        const isHighCensoringFraction = result.isHighCensoringFraction === true;

        const nextRiskRecord: Record<string, unknown> = {
            ...((this.riskDetails || {}) as object),
            uninformativeDistribution: result.uninformativeDistribution ?? null,
            informativeDistribution: result.informativeDistribution ?? null,
            hasInformativePrior,
            HasInformativePrior: hasInformativePrior,
            bayesianDecisionValue: decisionValue,
            bayesianDecisionStatisticValue: decisionValue,
            decisionValue,
            bayesianNoOelReason: noOelReason,
            noOelReason,
            hasOel,
            HasOel: hasOel,
            isHighCensoringFraction,
            IsHighCensoringFraction: isHighCensoringFraction,
            bayesianIsStale: false,
            isBayesianResultStale: false,
            isStale: false,
            priorMode,
            // Fix 2: record the prior mode that actually ran so the follow-up
            // prepareBayesianViewModel() stays consistent instead of reverting to the
            // jurisdiction default.
            [this.bayesianPriorPayloadKey]: priorMode === 1,
        };

        this.riskDetails = {
            ...this.riskDetails,
            ...(nextRiskRecord as Partial<RiskRegister>),
        };

        // The completed run is the new source of truth for the toggle, so drop any
        // in-session override flag and let prepareBayesianViewModel read the run mode.
        this.bayesianPriorSelectionDirty = false;
        this.prepareBayesianViewModel();
        this.updateRiskTableData();
    }

    private updateBayesianRunButtonLabel(): void {
        if (this.bayesianRunLoading) {
            this.bayesianRunButtonLabel = "Running Bayesian Analysis...";
            return;
        }

        const hasPosteriorData =
            this.bayesianDecisionValue != null ||
            this.bayesianDistributionUninformative.some((item) => item.probability > 0) ||
            this.bayesianDistributionInformative.some((item) => item.probability > 0);

        this.bayesianRunButtonLabel =
            this.bayesianResultStale || hasPosteriorData
                ? "Rerun Bayesian Analysis"
                : "Run Bayesian Analysis";
    }

    setControlFields() {
        const displayFields: string[] = [
            nameOf<Control>((x) => x.name),
            nameOf<Control>((x) => x.controlHierarchyBucketName),
        ];
        const labelMap: Record<string, string> = {
            [nameOf<Control>((x) => x.name)]: "Name",
            [nameOf<Control>((x) => x.controlHierarchyBucketName)]:
                "Control Bucket",
        };

        this.controlAssessmentField = new MultiSelectionFormField({
            property: nameOf<RiskRegister>((x) => x.riskAssessmentControlIds),
            name: "Existing Controls",
            required: false,
            options: this.controls,
            columnSpan: new ColumnSpan(1, 1),
            optionTemplateFields: displayFields,
            optionTemplateLabelMap: labelMap,
            onChange: (event) => {
                this.linkControlsDialog(true);
            },
            searchComponent: ControlsListComponent,
        });
        this.controlTreatmentField = new MultiSelectionFormField({
            property: nameOf<RiskRegister>((x) => x.riskTreatmentControlIds),
            name: "Risk Treatment Actions",
            required: false,
            options: this.controls,
            columnSpan: new ColumnSpan(1, 1),
            optionTemplateFields: displayFields,
            optionTemplateLabelMap: labelMap,
            onChange: (event) => {
                this.linkControlsDialog();
            },
            searchComponent: ControlsListComponent,
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes["parentAssessmentStatus"]) {
            this.isParentAssessmentApproved =
                this.parentAssessmentStatus === AssessmentStatusEnum.Approved;
            this.canRecalculate = !this.isParentAssessmentApproved;
            this.refreshAttachmentState();
        }
        if (changes["id"] && this.id) {
            this.riskDetails = null;
            this.qualitativeConfig = undefined;
            this.initializeQualitativeOptions(true);
            this.loading = true;
            this.loadRiskDetails();
            this.attachmentsDataSource.data = [];
            this.attachmentCount = 0;
            this.loadAttachments();
        }
    }

    loadAttachments(): void {
        const riskRegisterId = this.id;
        if (!riskRegisterId) return;

        this.attachmentsLoading = true;
        this.refreshAttachmentState();
        forkJoin({
            attachments: this.riskRegisterService.getAttachments(riskRegisterId),
            users: this.userService.getAll(),
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({ attachments, users }) => {
                    if (riskRegisterId !== this.id) return;
                    this.attachmentsDataSource.data = attachments.map((attachment) =>
                        this.toAttachmentRow(attachment, users),
                    );
                    this.attachmentCount = attachments.length;
                    this.attachmentsLoading = false;
                    this.refreshAttachmentState();
                },
            });
    }

    openAddAttachmentDialog(): void {
        if (!this.canAddAttachment || !this.riskDetails) return;

        const riskRegisterId = this.id;
        const data: IAttachmentsFormDialogData = {
            assetType: AssetTypeEnum.IHAttachments,
            organizationLevelId: this.riskDetails.organizationLevelId,
            showDescription: true,
            allowedFileTypes: RISK_REGISTER_ATTACHMENT_FILE_TYPES,
            maxFileSizeMB: RISK_REGISTER_ATTACHMENT_MAX_FILE_SIZE_MB,
            attachFunction: (payload: CreatePayload<FileAttachment>) =>
                this.fileAttachmentService.create(payload).pipe(
                    switchMap((file) =>
                        this.riskRegisterService.addAttachment({ riskRegisterId, fileAttachmentId: file.id }),
                    ),
                ),
        };

        this.dialog
            .open(AttachmentsDialogComponent, {
                data,
                width: "460px",
                panelClass: "attachments-dialog",
                autoFocus: false,
            })
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (attached: FileAttachment | undefined) => {
                    if (attached) this.loadAttachments();
                },
            });
    }

    confirmRemoveAttachment(attachment: RiskRegisterAttachmentRow): void {
        if (this.attachmentsLocked) return;

        const request = { riskRegisterId: this.id, fileAttachmentId: attachment.id };
        this.dialog
            .open(DeleteDialog, {
                data: {
                    id: attachment.id,
                    title: "Attachment",
                    customDelete: () => this.riskRegisterService.removeAttachment(request),
                },
                width: "440px",
                disableClose: true,
            })
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (removed: boolean | undefined) => {
                    if (removed) this.loadAttachments();
                },
            });
    }

    openAttachment(attachment: FileAttachment): void {
        if (!openFileInNewTab(attachment.url)) {
            this.toastService.displayErrorMessage("File URL not available. Please contact support", true);
        }
    }

    private toAttachmentRow(attachment: FileAttachment, users: Lookup[]): RiskRegisterAttachmentRow {
        return {
            ...attachment,
            typeLabel: attachment.fileType != null ? this.commonService.getEnumName(FileTypeEnum, attachment.fileType) : "-",
            sizeLabel: formatFileSizeInMB(attachment.fileSizeInBytes),
            uploadedByName: users.find((user) => user.id?.toString() === attachment.createdByUserId)?.name ?? "-",
        };
    }

    private refreshAttachmentState(): void {
        this.attachmentsLocked = this.isApproved;
        this.canAddAttachment =
            !this.attachmentsLocked &&
            !this.attachmentsLoading &&
            this.attachmentCount < RISK_REGISTER_MAX_ATTACHMENTS;
    }

    loadComponentData() {
        this.parameterService
            .getById(this.selectedParameter.id)
            .subscribe((res) => {
                this.parameter = res;
            });
    }

    get hasParameterFilter(): boolean {
        return true;
    }

    initializeQualitativeOptions(forceFallback = false, onDone?: () => void): void {
        if (forceFallback || !this.qualitativeConfig) {
            // DASH-2381: clear stale ranges/levels from a prior jurisdiction
            // config before falling back to defaults, so they don't carry over.
            this.likelihoodFactorsByRange.clear();
            this.likelihoodLevels = [];
            this.exceedanceProbabilityList = getEnumList(
                ExceedanceProbabilityExposureEnum,
            ).map((res) => ({
                label: res,
                value: ExceedanceProbabilityExposureEnum[res],
            }));
            this.frequencyExposureList = getEnumList(FrequencyExposureEnum).map(
                (res) => ({
                    label: res,
                    value: FrequencyExposureEnum[res],
                }),
            );
            this.likelihoodMapping = QualitativeMetrics as LikelihoodMapping[];
        } else {
            this.exceedanceProbabilityList = this.qualitativeConfig.exceedanceProbabilityOptions || [];
            this.frequencyExposureList = this.qualitativeConfig.frequencyExposureOptions || [];
            this.likelihoodMapping = this.qualitativeConfig.likelihoodMapping || [];
        }

        if (this.riskDetails) {
            this.setLikelihoodScore();
            this.setResidualLikelihoodScore();
        }

        this.refreshLikelihoodLevelDescriptionsCache();
        this.refreshQuantitativeLikelihoodLevelsCache();
        this.buildMatrixData();
        onDone?.();
    }

    loadQualitativeConfig(jurisdictionId: string, onDone?: () => void): void {
        if (!jurisdictionId) {
            this.initializeQualitativeOptions(true);
            this.loadConsequenceRatings(null);
            this.clearJurisdictionBayesianConfig();
            onDone?.();
            return;
        }

        this.jurisdictionRiskConfigService
            .getByJurisdictionId(jurisdictionId)
            .subscribe({
                next: (config) => {
                    // API returns null if jurisdiction has no risk config, treat as unconfigured
                    if (!config) {
                        // initializeQualitativeOptions(true) clears
                        // likelihoodFactorsByRange and likelihoodLevels itself.
                        this.qualitativeConfig = undefined;
                        this.clearJurisdictionBayesianConfig();
                        this.initializeQualitativeOptions(true, onDone);
                        return;
                    }
                    this.likelihoodFactorsByRange = this.getLikelihoodFactorsByRange(
                        config?.exposureLikelihoodLevels,
                    );
                    this.qualitativeConfig = this.toQualitativeConfig(
                        config.qualitativeConfig,
                    );
                    this.applyJurisdictionBayesianConfig(
                        this.parseJurisdictionBayesianConfig(config),
                    );
                    this.loadConsequenceRatings(config?.exposureLikelihoodLevels);
                    // initializeQualitativeOptions will recalculate scores and call onDone
                    this.initializeQualitativeOptions(false, onDone);
                },
                error: (error) => {
                    if (!environment.production) {
                        console.warn('Failed to load qualitative config:', error);
                    }
                    // initializeQualitativeOptions(true) clears the stale ranges/levels.
                    this.loadConsequenceRatings(null);
                    this.clearJurisdictionBayesianConfig();
                    this.initializeQualitativeOptions(true, onDone);
                },
            });
    }

    private applyJurisdictionBayesianConfig(config: BayesianConfig): void {
        this.bayesianJurisdictionConfig = config;
        this.bayesianEnabled =
            config.isBayesianAnalysisEnabled ??
            config.enabled ??
            false;
    }

    private clearJurisdictionBayesianConfig(): void {
        this.bayesianJurisdictionConfig = {};
        this.bayesianEnabled = false;
    }

    private parseJurisdictionBayesianConfig(
        config: Pick<
            JurisdictionRiskConfig,
            | "bayesianConfig"
            | "defaultPriorMode"
            | "defaultPriorWidth"
            | "enableCensoringGuardrail"
            | "configurationNotes"
            | "isBayesianAnalysisEnabled"
        >,
    ): BayesianConfig {
        if (
            config.defaultPriorMode != null ||
            config.defaultPriorWidth != null ||
            config.enableCensoringGuardrail != null ||
            config.configurationNotes != null ||
            config.isBayesianAnalysisEnabled != null
        ) {
            return {
                enabled: config.isBayesianAnalysisEnabled ?? undefined,
                isBayesianAnalysisEnabled: config.isBayesianAnalysisEnabled,
                defaultPriorMode: config.defaultPriorMode,
                defaultPriorWidth: config.defaultPriorWidth,
                enableCensoringGuardrail: config.enableCensoringGuardrail,
                configurationNotes: config.configurationNotes,
                notes: config.configurationNotes,
            };
        }

        if (!config.bayesianConfig) {
            return {};
        }

        try {
            const parsed = JSON.parse(config.bayesianConfig) as BayesianConfig;
            return {
                enabled: parsed.isBayesianAnalysisEnabled ?? parsed.enabled,
                isBayesianAnalysisEnabled: parsed.isBayesianAnalysisEnabled,
                framework: parsed.framework,
                defaultPriorMode: parsed.defaultPriorMode,
                defaultPriorWidth: parsed.defaultPriorWidth,
                informativePriorSource: parsed.informativePriorSource,
                enableCensoringGuardrail: parsed.enableCensoringGuardrail,
                configurationNotes: parsed.configurationNotes,
                notes: parsed.configurationNotes || parsed.notes,
            };
        } catch {
            return {};
        }
    }

    private hydrateBayesianStoredState(): void {
        if (!this.id) {
            // No stored result to fetch — still build the view model once.
            this.prepareBayesianViewModel();
            return;
        }

        forkJoin({
            // Both reads catch + toast + default in the service (read-path convention).
            storedResult: this.riskRegisterService.getStoredBayesianResult(this.id),
            isStale: this.riskRegisterService.isBayesianResultStale(this.id),
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({ storedResult, isStale }) => {
                    this.mergeStoredBayesianResult(storedResult, isStale);
                    this.prepareBayesianViewModel();
                    this.updateRiskTableData();
                    // Fix 3: the stored result resolves after the first Bayesian-tab
                    // click, when the initial render bailed with no data. Now that the
                    // distributions are loaded, re-trigger the render.
                    if (this.selectedRiskTab === 1) {
                        this.scheduleBayesianChartRender();
                    }
                },
            });
    }

    private mergeStoredBayesianResult(
        storedResult: BayesianAnalysisStoredResultResponse | null,
        isStale: boolean,
    ): void {
        const stalePayload = {
            bayesianIsStale: isStale,
            isBayesianResultStale: isStale,
            isStale,
            IsStale: isStale,
        };

        if (!storedResult?.resultJson) {
            this.riskDetails = {
                ...this.riskDetails,
                ...stalePayload,
            };
            return;
        }

        const parsedResult = this.tryParseJson(storedResult.resultJson) as
            | Record<string, unknown>
            | undefined;

        if (!parsedResult) {
            this.riskDetails = {
                ...this.riskDetails,
                ...stalePayload,
            };
            return;
        }

        const mergedRiskDetails: RiskRegister = {
            ...this.riskDetails,
            ...(parsedResult as Partial<RiskRegister>),
            ...stalePayload,
        };

        mergedRiskDetails.priorMode = storedResult.priorModeUsed;
        this.riskDetails = mergedRiskDetails;
    }

    private getLikelihoodFactorsByRange(
        exposureLikelihoodLevelsJson: string | null | undefined,
    ): Map<string, string> {
        const map = new Map<string, string>();
        this.likelihoodLevels = [];
        this.likelihoodLevelColors.clear();

        if (!exposureLikelihoodLevelsJson) {
            return map;
        }

        try {
            const parsed = JSON.parse(exposureLikelihoodLevelsJson) as ExposureLikelihoodLevelsStorage;
            Object.entries(parsed)
                .filter(([key, value]) => key !== 'ConsequenceRatings' && typeof value === 'object' && value !== null && !Array.isArray(value))
                .forEach(([key, value]) => {
                    const level = value as Partial<ExposureLikelihoodLevelConfig>;
                    const min = Number(level.QualThresholds?.[0]);
                    const max = Number(level.QualThresholds?.[1]);
                    const minQuant = Number(level.QuantThreshold_OEL?.[0]);
                    const maxQuant = Number(level.QuantThreshold_OEL?.[1]);

                    if (Number.isFinite(min) && Number.isFinite(max)) {
                        map.set(this.buildLikelihoodRangeKey(min, max), key);
                    }
                    // Read the configured level straight from the likelihood table so
                    // the info-icon table shows exactly what's configured (Name +
                    // Description), independent of the qualitative likelihoodMapping.
                    const score = Number(key);
                    this.likelihoodLevels.push({
                        score: Number.isFinite(score) ? score : 0,
                        name: level.Name || key,
                        description: level.Description || '',
                        quantitativeName: level.QuantitativeName?.trim() || '',
                        minQuantThreshold: Number.isFinite(minQuant) ? minQuant : null,
                        maxQuantThreshold: Number.isFinite(maxQuant) ? maxQuant : null,
                    });
                    if (level.Color) {
                        this.likelihoodLevelColors.set(
                            Number.isFinite(score) ? score : 0,
                            level.Color,
                        );
                    }
                });
            this.likelihoodLevels.sort((a, b) => a.score - b.score);
        } catch {
            this.likelihoodLevels = [];
            return map;
        }

        return map;
    }

    private buildLikelihoodRangeKey(minProduct: number, maxProduct: number): string {
        return `${minProduct}:${maxProduct}`;
    }

    private loadConsequenceRatings(exposureLikelihoodLevelsJson: string | null | undefined): void {
        this.consequenceRatings = parseConsequenceRatings(exposureLikelihoodLevelsJson);
        this.buildMatrixData();
    }

    // The Consequence Rating used during the assessment may fall outside the Jurisdiction's
    // configured Consequence Rating ranges (e.g. ranges changed after assessment). Without a
    // matching option the mat-select renders blank, hiding the assessed value. Append the raw
    // value as a synthetic option so the dropdown always shows what was actually assessed.
    private ensureAssessedConsequenceRatingOption(): void {
        const value = this.riskDetails?.consequenceRating;
        if (value == null || !Number.isFinite(Number(value))) {
            return;
        }

        const numericValue = Number(value);
        const alreadyPresent = this.consequenceRatings.some(
            (rating) => rating.value === numericValue,
        );
        if (alreadyPresent) {
            return;
        }

        this.consequenceRatings = [
            ...this.consequenceRatings,
            { value: numericValue, label: String(numericValue), description: undefined },
        ].sort((a, b) => a.value - b.value);
    }

    // DASH-2377: the quantitative Likelihood Rating is calculated on the backend
    // from the linked sample data (quantitativeLikelihoodScore). Resolve the
    // configured level from that authoritative score first; only fall back to
    // matching the result %-of-OEL against the configured thresholds when no
    // score is available, so the displayed rating always reflects the computed
    // value rather than a re-derivation that can miss out-of-range percentages.
    private resolveQuantitativeLikelihoodLevel(
        quantitativeResultPercentOfOEL: number | null | undefined,
    ) {
        const quantPercent =
            quantitativeResultPercentOfOEL != null &&
            Number.isFinite(Number(quantitativeResultPercentOfOEL))
                ? Number(quantitativeResultPercentOfOEL)
                : NaN;
        const score = this.riskDetails?.quantitativeLikelihoodScore;
        const numericScore =
            score != null && Number.isFinite(Number(score))
                ? Number(score)
                : null;
        return (
            (numericScore != null
                ? this.likelihoodLevels.find((level) => level.score === numericScore)
                : undefined) ??
            this.likelihoodLevels.find(
                (level) =>
                    Number.isFinite(quantPercent) &&
                    level.minQuantThreshold != null &&
                    level.maxQuantThreshold != null &&
                    quantPercent >= level.minQuantThreshold &&
                    quantPercent <= level.maxQuantThreshold,
            )
        );
    }

    private resolveQualitativeLikelihoodLevel(
        qualitativeLikelihoodScore: number | null | undefined,
    ) {
        const numericScore =
            qualitativeLikelihoodScore != null &&
            Number.isFinite(Number(qualitativeLikelihoodScore))
                ? Number(qualitativeLikelihoodScore)
                : null;
        if (numericScore == null || numericScore === 0) {
            return undefined;
        }
        return this.likelihoodLevels.find((level) => level.score === numericScore);
    }

    private updateExposureLabel(): void {
        const naText = this.translateService.getDynamicTranslation("N/A");
        const method = this.riskDetails?.methodOfAssessment;

        // --- Quantitative section (exposureBandText / exposureBandRange) ---
        // Always the band derived from the QUANTITATIVE method itself, regardless of which Method of
        // Assessment the register has selected. A qualitative register that was never measured has no
        // quantitative likelihood score and no % of OEL, so no level resolves and the section reads
        // N/A — it must never borrow the qualitative band, which is not a quantitative result.
        const quantLevel = this.resolveQuantitativeLikelihoodLevel(
            this.riskDetails?.quantitativeResultPercentOfOEL,
        );
        const quantBand = resolveExposureBandLabel(quantLevel, naText);
        this.exposureBandText = quantBand.bandName;
        this.exposureBandRange = quantBand.bandRange;
        this.quantitativeLikelihoodText = (quantLevel?.name || "").trim() || naText;

        // --- Header (exposureLabelValue / headerBandRange) ---
        // Band resolved from the register's OWN assessment method, with the name and the range taken
        // from that single level (crit. 8 / H1 — the two lines can never describe different bands).
        if (method === AssessmentMethodEnum.Quantitative) {
            this.exposureLabelValue = quantBand.bandName;
            this.headerBandRange = quantBand.bandRange;
            this.exposureLabelColor =
                quantLevel != null
                    ? this.likelihoodLevelColors.get(quantLevel.score) ?? ""
                    : "";
            return;
        }

        if (method === AssessmentMethodEnum.Qualitative) {
            const qualLevel = this.resolveQualitativeLikelihoodLevel(
                this.riskDetails?.qualitativeLikelihoodScore,
            );
            // DASH-3112 (reviewer/CTO request): show the resolved level's configured % of OEL band
            // range beside the band word for qualitative registers too, in line with the quantitative
            // header. The range comes from the same single level as the band word (H1).
            const qualBand = resolveExposureBandLabel(qualLevel, naText);
            this.exposureLabelValue = qualBand.bandName;
            this.headerBandRange = qualBand.bandRange;
            this.exposureLabelColor =
                qualLevel != null
                    ? this.likelihoodLevelColors.get(qualLevel.score) ?? ""
                    : "";
            return;
        }

        // Hazard Does Not Apply — no band to resolve. DASH-2979 (G19): a null ExposureRisk is the
        // neutral "not assessed" state and must read distinctly, never blank/silently as Low.
        this.exposureLabelValue =
            this.riskDetails?.exposureRisk == null
                ? this.translateService.getDynamicTranslation("Not assessed")
                : this.exposureRisk[this.riskDetails.exposureRisk] || "";
        this.headerBandRange = "";
        this.exposureLabelColor = "";
    }

    private toQualitativeConfig(
        rawQualitativeConfig?: string,
    ): QualitativeConfig | undefined {
        const parsedConfig = this.tryParseJson(rawQualitativeConfig);

        if (this.isResponseQualitativeConfig(parsedConfig)) {
            return this.fromResponseQualitativeConfig(parsedConfig);
        }

        if (this.isLocalQualitativeConfig(parsedConfig)) {
            return parsedConfig;
        }


        return undefined;
    }

    private isResponseQualitativeConfig(value: unknown): value is QualitativeConfigApi {
        if (!value || typeof value !== "object") {
            return false;
        }

        const candidate = value as Partial<QualitativeConfigApi>;
        return (
            Array.isArray(candidate.dimensions) &&
            Array.isArray(candidate.likelihoodMapping)
        );
    }

    private isLocalQualitativeConfig(value: unknown): value is QualitativeConfig {
        if (!value || typeof value !== "object") {
            return false;
        }

        const candidate = value as Partial<QualitativeConfig>;
        return (
            Array.isArray(candidate.exceedanceProbabilityOptions) &&
            Array.isArray(candidate.frequencyExposureOptions) &&
            Array.isArray(candidate.likelihoodMapping)
        );
    }

    private fromResponseQualitativeConfig(config: QualitativeConfigApi): QualitativeConfig {
        const dimensions = Array.isArray(config.dimensions) ? config.dimensions : [];
        const likelihoodMapping = Array.isArray(config.likelihoodMapping)
            ? config.likelihoodMapping
            : [];

        return {
            id: '',
            jurisdictionId: this.jurisdictionId ?? '',
            exceedanceProbabilityOptions: dimensions[0]?.options ?? [],
            frequencyExposureOptions: dimensions[1]?.options ?? [],
            likelihoodMapping: likelihoodMapping.map((mapping) => ({
                exposureFrequencyScore: [
                    mapping.minProduct,
                    mapping.maxProduct,
                ],
                likelihoodScore: mapping.likelihoodScore,
                classification: mapping.classification,
                factor: this.likelihoodFactorsByRange.get(
                    this.buildLikelihoodRangeKey(mapping.minProduct, mapping.maxProduct),
                ),
                description: mapping.description,
            })),
            exceedanceProbabilityName: dimensions[0]?.name,
            frequencyExposureName: dimensions[1]?.name,
            exceedanceProbabilityDescription: dimensions[0]?.description,
            frequencyExposureDescription: dimensions[1]?.description,
        };
    }

    get exceedanceProbabilityLabel(): string {
        return this.qualitativeConfig?.exceedanceProbabilityName || 'Exceedance Probability';
    }

    get frequencyExposureLabel(): string {
        return this.qualitativeConfig?.frequencyExposureName || 'Frequency of Exposure';
    }

    // DASH-2278: rebuild the full set of likelihood levels (one row per distinct
    // likelihood score) shown in the hover-table next to the likelihood rating.
    // Pre-computed into likelihoodLevelDescriptionsCache (no template getter calls).
    private refreshLikelihoodLevelDescriptionsCache(): void {
        // Descriptions can be configured in two places: on the exposureLikelihoodLevels
        // (the "likelihood table") and/or on the qualitative likelihoodMapping. Build a
        // lookup from the mapping (keyed by both score and name) so a level whose own
        // Description is blank can still surface a description configured on the mapping.
        const mappingDescByScore = new Map<number, string>();
        const mappingDescByName = new Map<string, string>();
        for (const mapping of this.likelihoodMapping) {
            if (!mapping.description) continue;
            if (!mappingDescByScore.has(mapping.likelihoodScore)) {
                mappingDescByScore.set(mapping.likelihoodScore, mapping.description);
            }
            const name = mapping.factor || mapping.classification;
            if (name && !mappingDescByName.has(name)) {
                mappingDescByName.set(name, mapping.description);
            }
        }

        // Primary source: the configured likelihood levels (exposureLikelihoodLevels),
        // which carry the Name exactly as set in the likelihood table. Fill in the
        // description from the level itself first, then fall back to the mapping.
        if (this.likelihoodLevels.length) {
            this.likelihoodLevelDescriptionsCache = this.likelihoodLevels.map((level) => ({
                ...level,
                description:
                    level.description ||
                    mappingDescByScore.get(level.score) ||
                    mappingDescByName.get(level.name) ||
                    '',
            }));
            return;
        }

        // Fallback: derive level names from the qualitative likelihoodMapping when no
        // explicit likelihood levels are configured.
        const byScore = new Map<number, { score: number; name: string; description: string }>();
        for (const mapping of this.likelihoodMapping) {
            if (byScore.has(mapping.likelihoodScore)) continue;
            const factor = mapping.factor || mapping.classification || `${mapping.likelihoodScore}`;
            byScore.set(mapping.likelihoodScore, {
                score: mapping.likelihoodScore,
                name: factor,
                description: mapping.description || '',
            });
        }
        this.likelihoodLevelDescriptionsCache = Array.from(byScore.values()).sort(
            (a, b) => a.score - b.score,
        );
    }

    // Build the full set of configured Exposure Likelihood Levels shown in
    // the Quantitative Likelihood Rating info-icon table. Each row carries the
    // Likelihood Value Factor (score), the Quantitative Name, and the Quant % OEL range
    // exactly as configured on the jurisdiction's exposureLikelihoodLevels. A level with
    // no Quantitative Name falls back to its level name, then to N/A in the template; a
    // level with no Quant % OEL range is shown as N/A in the template.
    private refreshQuantitativeLikelihoodLevelsCache(): void {
        this.quantitativeLikelihoodLevelsCache = this.likelihoodLevels.map((level) => {
            const hasRange =
                level.minQuantThreshold != null && level.maxQuantThreshold != null;
            return {
                factor: level.score,
                quantitativeName: level.quantitativeName || level.name || '',
                quantOelRange: hasRange
                    ? `${level.minQuantThreshold}–${level.maxQuantThreshold}%`
                    : '',
            };
        });
    }

    get residualRiskExceedanceLabel(): string {
        return `Residual Risk ${this.exceedanceProbabilityLabel}`;
    }

    get residualRiskFrequencyLabel(): string {
        return `Residual Risk ${this.frequencyExposureLabel}`;
    }

    private tryParseJson(value?: string): unknown {
        if (!value) {
            return undefined;
        }

        try {
            return JSON.parse(value);
        } catch {
            return undefined;
        }
    }

    filterOelSections() {
        this.filteredOelSections = getEnumList(OelSections).filter((res) => {
            switch (OelSections[res]) {
                case OelSections["Sample Results"]:
                    return Boolean(
                        this.riskDetails.riskRegisterSamples?.length,
                    );
                case OelSections["Goodness of fit"]:
                    return Boolean(this.riskDetails.qqPlot);
                case OelSections["Log-normal fit"]:
                    return Boolean(this.riskDetails.pdfPlot);
                default:
                    return false;
            }
        });
        const restoredIndex = this.chartTabToRestore
            ? this.filteredOelSections.indexOf(this.chartTabToRestore)
            : -1;
        this.chartTabToRestore = null;
        this.selectedTabIndex = Math.max(restoredIndex, 0);
        if (this.filteredOelSections.length) {
            const mockEvent = { index: this.selectedTabIndex } as MatTabChangeEvent;
            this.onTabChange(mockEvent);
        }
        this.computePlotEmptyStateMessage();
    }

    // Marking patches the sample and recalculates the register, so it needs edit rights on both and an
    // unlocked register.
    get canMarkOutliers(): boolean {
        const update = gridToPermissionFlags[GridPermissions.Update];
        return (
            !this.isApproved &&
            this.permissionService.hasPermissionFlag(RolePermissionObjects.RiskRegister, update) &&
            this.permissionService.hasPermissionFlag(RolePermissionObjects.Sample, update)
        );
    }

    markChartSampleAsOutlier(sampleId: string): void {
        if (!this.canMarkOutliers) {
            return;
        }
        const chartTab = this.filteredOelSections[this.selectedTabIndex] ?? null;
        this.sampleOutlierService
            .promptOutlierReason()
            .pipe(
                switchMap((reason) =>
                    this.sampleOutlierService.setOutlier(sampleId, { isOutlier: true, outlierReason: reason }).pipe(
                        catchError(() => {
                            this.toastService.displayErrorMessage(
                                this.translateService.getDynamicTranslation("Failed to mark sample as outlier"),
                                true,
                            );
                            return EMPTY;
                        }),
                    ),
                ),
                tap(() =>
                    this.toastService.showSuccess(
                        this.translateService.getDynamicTranslation("Sample marked as outlier"),
                        true,
                    ),
                ),
                switchMap(() =>
                    this.sampleOutlierService.recalculateRegister(this.id).pipe(
                        catchError(() => {
                            this.toastService.displayErrorMessage(
                                this.translateService.getDynamicTranslation(
                                    "The sample was updated but the statistics could not be recalculated. The register is now out of date — use Recalculate to refresh it.",
                                ),
                                true,
                            );
                            return of(null);
                        }),
                    ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                this.chartTabToRestore = chartTab;
                this.loadRiskDetails();
                this.updateGridEmit.emit(true);
            });
    }

    // Joins each Q-Q point to its sample by sample number. A number shared by more than one sample cannot be
    // resolved to one sample, so that point shows its tooltip without the mark action.
    buildQqChartRows(qqplot: QqPlot): QqChartRow[] {
        const samplesByNumber = new Map<string, RiskRegisterSample[]>();
        (this.riskDetails?.riskRegisterSamples ?? []).forEach((sample) => {
            if (sample.sampleNumber) {
                samplesByNumber.set(sample.sampleNumber, [
                    ...(samplesByNumber.get(sample.sampleNumber) ?? []),
                    sample,
                ]);
            }
        });
        const canMark = this.canMarkOutliers;
        const unitSuffix = this.agentUnitSymbolSuffix();
        return qqplot.theoreticalQuantiles.map((theoreticalValue, index) => {
            const empirical = this.toDisplayUnit(qqplot.empiricalQuantiles[index]);
            const sampleNumber = qqplot.sampleNumbers?.[index] ?? null;
            const matches = sampleNumber ? (samplesByNumber.get(sampleNumber) ?? []) : [];
            const sample = new Set(matches.map((match) => match.sampleId)).size === 1 ? matches[0] : undefined;
            const sampleId = canMark && sample ? sample.sampleId.toString() : null;
            const details = {
                sampleNumber: sampleNumber ?? "-",
                date: sample?.startDate
                    ? this.dateFormatService.formatTimestampToString(
                          sample.startDate,
                          this.dateFormatService.userDateFormat,
                      )
                    : "-",
                reported: sample
                    ? this.formatReportedDisplay(sample)
                    : `${empirical ?? "-"}${unitSuffix}`,
                nonDetect: qqplot.censorTypes?.[index] === SampleCensorType.LessThan,
                markable: sampleId != null,
            };
            return {
                theoretical: this.toDisplayUnit(theoreticalValue),
                empirical,
                sampleId,
                tooltip: this.buildQqTooltipText(details),
                ariaLabel: this.buildQqAriaLabel(details),
            };
        });
    }

    private buildQqTooltipText(details: QqPointDetails): string {
        const lines = [
            `${this.translateService.getDynamicTranslation("Sample Number")}: [bold]${escapeChartText(details.sampleNumber)}[/]`,
            `${this.translateService.getDynamicTranslation("Date Collected")}: [bold]${escapeChartText(details.date)}[/]`,
            `${this.translateService.getDynamicTranslation("Reported")}: [bold]${escapeChartText(details.reported)}[/]`,
        ];
        if (details.nonDetect) {
            lines.push(`[bold]${this.translateService.getDynamicTranslation("Non-detect")}[/]`);
        }
        if (details.markable) {
            lines.push(this.translateService.getDynamicTranslation("Click to mark as outlier"));
        }
        return lines.join("\n");
    }

    private buildQqAriaLabel(details: QqPointDetails): string {
        return [
            `${this.translateService.getDynamicTranslation("Sample Number")}: ${details.sampleNumber}`,
            `${this.translateService.getDynamicTranslation("Date Collected")}: ${details.date}`,
            `${this.translateService.getDynamicTranslation("Reported")}: ${details.reported}`,
            ...(details.nonDetect ? [this.translateService.getDynamicTranslation("Non-detect")] : []),
            ...(details.markable ? [this.translateService.getDynamicTranslation("Press Enter to mark as outlier")] : []),
        ].join(", ");
    }

    private agentUnitSymbolSuffix(): string {
        const unit = this.riskDetails?.agentUnits;
        return unit != null ? ` ${UnitSymbols[unit]}` : "";
    }

    /**
     * G19/DASH-2977 (FE-3, AC6): when the register has no plot to show the chart div collapses to
     * nothing, which reads as a bug — especially now `ExcludeNonDetects` makes small-N common.
     * DASH-3340: the reason is now authoritative from the BE (`distributionPlotUnavailableReason`,
     * recomputed on every read), so name the actual cause rather than inferring it on the FE.
     */
    private computePlotEmptyStateMessage(): void {
        const tt = (key: string) => this.translateService.getDynamicTranslation(key);
        // AC6 targets the *distribution* plots (Goodness-of-fit / Log-normal fit). Gate on the plots
        // themselves — NOT `filteredOelSections`, which still admits the Sample Results tab because the BE
        // deliberately retains excluded non-detects in the register's sample list, so that list is truthy
        // even when no distribution plot can be drawn (was the dead-code bug).
        const hasPlot = !!(this.riskDetails?.qqPlot || this.riskDetails?.pdfPlot);
        if (hasPlot || !this.riskDetails) {
            this.plotEmptyStateMessage = '';
            return;
        }
        const genericMessage = tt('At least 3 samples are required to draw distribution plots.');
        switch (this.riskDetails.distributionPlotUnavailableReason) {
            case DistributionPlotUnavailableReason.OelUnavailable:
                this.plotEmptyStateMessage = tt('An occupational exposure limit is required to draw distribution plots.');
                break;
            case DistributionPlotUnavailableReason.NonPositiveValue:
                this.plotEmptyStateMessage = tt('A sample value is zero or negative, so distribution plots cannot be drawn.');
                break;
            case DistributionPlotUnavailableReason.ZeroVariance:
                this.plotEmptyStateMessage = tt('All sample values are identical, so distribution plots cannot be drawn.');
                break;
            case DistributionPlotUnavailableReason.InsufficientSamples:
                // The BE classifier runs on the POST-exclusion statistical set, so an all-excluded /
                // below-threshold register is reported as InsufficientSamples — true, but the exclusion
                // effect explains *why* the count dropped and is the more specific message. Keep it for
                // that case; every other reason is never caused by exclusion, so it wins outright.
                this.plotEmptyStateMessage = this.insufficientSamplesMessage(tt, genericMessage);
                break;
            default:
                // null => plots were produced (handled by hasPlot above); any other value is an
                // unexpected/future reason we cannot name. Use a neutral message rather than asserting
                // a specific cause (e.g. the sample-count copy) that may be wrong for that reason.
                this.plotEmptyStateMessage = tt('Distribution plots cannot be drawn for this register.');
        }
    }

    private insufficientSamplesMessage(tt: (key: string) => string, genericMessage: string): string {
        // Only attribute the missing plot to exclusion when something was actually excluded. A register
        // that never had samples is classified `AllSamplesExcluded` by the BE regardless, so without this
        // guard it would falsely claim "all samples were non-detects" — fall through to the generic copy.
        const excludedAny = (this.riskDetails.excludedNonDetectCount ?? 0) > 0;
        switch (this.riskDetails.nonDetectExclusionEffect) {
            case NonDetectExclusionEffect.AllSamplesExcluded:
                return excludedAny
                    ? tt('All samples were non-detects and excluded, so distribution plots cannot be drawn.')
                    : genericMessage;
            case NonDetectExclusionEffect.BelowStatisticsThreshold:
                // Copy intentionally does NOT restate a sample count: the displayed N
                // (`quantitativeNumberOfSamples`) is pre-`FilterPositiveTwaValues` while this effect is
                // classified on the positive count, so a literal "fewer than 3" can contradict the N shown.
                return excludedAny
                    ? tt('Too few usable samples remain after excluding non-detects, so distribution plots cannot be drawn.')
                    : genericMessage;
            default:
                return genericMessage;
        }
    }

    loadRiskDetails() {
        this.bayesianPriorSelectionDirty = false;
        const colors = this.commonService.colors;
        const convertedNumber = (
            value: number | null | undefined,
            unit: number | null | undefined,
        ): number =>
            unit == null
                ? parseFloat((value ?? 0).toFixed(4))
                : parseFloat(
                      this.valueConversionService
                          .getParameterValueAsPerAgentUnit(value ?? 0, unit)
                          .toFixed(4),
                  );
        const convertedValue = (
            value: number | null | undefined,
            unit: number | null | undefined,
        ): string =>
            convertedNumber(value, unit) +
            " " +
            (unit != null ? UnitSymbols[unit] : "-");
        const convertRegisterSamples = (
            samples: RiskRegisterSample[] | undefined,
            agentUnits: number | null | undefined,
            oel: number,
            quantitativeDecisionStatisticValue: number,
            quantitativeGeometricMean: number,
        ): RiskRegisterSample[] =>
            (samples ?? []).map((val: RiskRegisterSample) => {
                val.value = convertedNumber(
                    val.sampleResults?.[0],
                    agentUnits,
                );
                // The substituted value shares SampleResults' storage basis, so it
                // gets the identical agent-unit conversion (÷1000 for mg/m3, no-op
                // for dB — the BE already back-converted noise). A null entry means
                // ExcludeNonDetects dropped this sample; keep it null so it is not
                // coerced to 0 and plotted as a real point.
                val.substitutedValue =
                    val.substitutedSampleResults?.[0] == null
                        ? null
                        : convertedNumber(
                              val.substitutedSampleResults[0],
                              agentUnits,
                          );
                val.unit =
                    agentUnits != null ? UnitSymbols[agentUnits] : "-";
                val.valueOel = convertedNumber(oel, agentUnits);
                val.valueQuantitativeDecisionStatisticValue =
                    convertedNumber(
                        quantitativeDecisionStatisticValue,
                        agentUnits,
                    );
                val.valueQuantitativeGeometricMean = convertedNumber(
                    quantitativeGeometricMean,
                    agentUnits,
                );
                return val;
            });

        this.riskRegisterService
            .getRiskRegister(this.id)
            .pipe(
                switchMap((risk) => {
                const oelLineValue = this.oelReferenceLineValue(risk);
                risk.riskRegisterSamples = convertRegisterSamples(
                    risk.riskRegisterSamples,
                    risk.agentUnits,
                    oelLineValue,
                    risk.quantitativeDecisionStatisticValue,
                    risk.quantitativeGeometricMean,
                );
                risk.outlierSamples = convertRegisterSamples(
                    risk.outlierSamples,
                    risk.agentUnits,
                    oelLineValue,
                    risk.quantitativeDecisionStatisticValue,
                    risk.quantitativeGeometricMean,
                );
                const parsedOelCorrectionParams = risk.oelCorrectionParams
                    ? (() => {
                          try {
                              const p =
                                  typeof risk.oelCorrectionParams === "string"
                                      ? JSON.parse(risk.oelCorrectionParams)
                                      : risk.oelCorrectionParams;
                              return {
                                  shiftLengthHours: p.shiftLengthHours ?? null,
                                  weeklyHours: p.weeklyHours ?? null,
                                  pressureMmHg: p.pressureMmHg ?? null,
                              };
                          } catch {
                              return null;
                          }
                      })()
                    : null;
                this.initialRiskDetails = {
                    ...risk,
                    oelCorrectionParams: parsedOelCorrectionParams,
                    convertedQuantitativeGeometricMean: convertedValue(
                        risk.quantitativeGeometricMean,
                        risk.agentUnits,
                    ),
                    convertedQuantitativeDecisionStatisticValue: convertedValue(
                        risk.quantitativeDecisionStatisticValue,
                        risk.agentUnits,
                    ),
                    // DASH-2808: MVUE shares the decision statistic's concentration unit and
                    // conversion factor, so show it converted with the same agent-unit symbol.
                    convertedQuantitativeMVUE: convertedValue(
                        risk.quantitativeMVUE,
                        risk.agentUnits,
                    ),
                    // DASH-3331: Sample Mean and 95th Percentile are concentrations, converted with the
                    // same agent-unit factor/symbol as the surrounding quantitative rows.
                    convertedQuantitativeMean: convertedValue(
                        risk.quantitativeMean,
                        risk.agentUnits,
                    ),
                    convertedQuantitative95thPercentile: convertedValue(
                        risk.quantitative95thPercentile,
                        risk.agentUnits,
                    ),
                    // Which distribution the engine actually used (not just which fit), read from
                    // the backend-resolved DistributionUsed enum. Null (qualitative registers,
                    // quantitative registers below the sample floor, or an enum value not yet known
                    // to the frontend) renders empty rather than a blank translation lookup.
                    distributionUsedLabel: (() => {
                        const key = resolveDistributionUsedKey(
                            risk.distributionUsed,
                        );
                        return key == null
                            ? null
                            : this.translateService.getDynamicTranslation(key);
                    })(),
                    convertedOEL: convertedValue(risk.oel, risk.agentUnits),
                    convertedCorrectedOEL: risk.correctedOEL
                        ? convertedValue(risk.correctedOEL, risk.agentUnits)
                        : null,
                    wTestOutput: this.translateService.getDynamicTranslation(
                        resolveDistributionLabelKey(risk.isNormal, risk.isLogNormal),
                    ),
                    imageSrc:
                        RiskRegisterAssessmentIconsEnum[risk.assessmentStatus],
                    statusColor: [
                        RiskRegisterAssessmentStatusEnum.Approved,
                        RiskRegisterAssessmentStatusEnum.AutoApproved,
                        RiskRegisterAssessmentStatusEnum.Submitted,
                    ].includes(risk.assessmentStatus)
                        ? colors.green
                        : [
                                RiskRegisterAssessmentStatusEnum.Exception,
                                RiskRegisterAssessmentStatusEnum.ApprovedWithException,
                                RiskRegisterAssessmentStatusEnum.Rejected,
                            ].includes(risk.assessmentStatus)
                          ? colors.red
                          : colors.greyDark,
                };
                this.riskDetails = {
                    ...this.initialRiskDetails,
                    methodOfAssessment: this.resolveMethodOfAssessment(
                        risk.methodOfAssessment,
                    ),
                };
                this.isQualitativeExpanded =
                    this.riskDetails.methodOfAssessment ===
                    AssessmentMethodEnum.Qualitative;
                this.computeQqGoodnessOfFit(this.riskDetails.qqPlot);
                this.originalConsequenceRating = this.riskDetails.consequenceRating;
                this.originalConsequenceRatingComment = this.riskDetails.consequenceRatingComment ?? null;
                this.consequenceRatingChanged = false;
                this.isConsequenceRatingCommentExpanded = false;
                this.updateHasConsequenceRatingComment(
                    this.riskDetails.consequenceRatingComment,
                );

                // Finalize setup after qualitative config resolves
                const finalize = () => {
                    this.ensureAssessedConsequenceRatingOption();
                    this.updateExposureLabel();
                    this.setRiskRegisterControls();
                    // hydrateBayesianStoredState() owns the Bayesian view-model refresh
                    // (it calls prepareBayesianViewModel once the stored result resolves),
                    // so we don't build it here first — that caused a double render.
                    this.hydrateBayesianStoredState();
                    this.updateRiskTableData();
                    this.filterOelSections();
                    this.getOtherFields();
                };

                // Load qualitative config - use provided jurisdictionId or fetch from RiskAssessment
                if (this.jurisdictionId) {
                    this.loadQualitativeConfig(this.jurisdictionId, finalize);
                    return of(null);
                }

                if (risk.riskAssessmentId) {
                    return this.riskAssessmentService.getById(risk.riskAssessmentId).pipe(
                        switchMap((assessment) => {
                            this.loadQualitativeConfig(assessment.jurisdictionId, finalize);
                            return of(null);
                        }),
                        catchError((error) => {
                            if (!environment.production) {
                                console.warn('Failed to load RiskAssessment for jurisdiction:', error);
                            }
                            this.initializeQualitativeOptions(true, finalize);
                            return of(null);
                        }),
                    );
                }

                // No jurisdiction context available — initializeQualitativeOptions(true)
                // clears likelihoodLevels, so updateExposureLabel (called inside finalize)
                // will fall back to showing raw % or N/A. This is intentional: without a
                // jurisdiction risk-config there are no configured thresholds to match.
                this.initializeQualitativeOptions(true, finalize);
                return of(null);
                }),
            )
            .subscribe({
                error: () => {
                    this.loading = false;
                },
            });
    }

    private resolveMethodOfAssessment(
        methodOfAssessment: number | null | undefined,
    ): AssessmentMethodEnum {
        const isValid = (Object.values(AssessmentMethodEnum) as number[]).includes(
            methodOfAssessment as number,
        );
        return isValid
            ? (methodOfAssessment as AssessmentMethodEnum)
            : AssessmentMethodEnum.Quantitative;
    }

    onMethodOfAssessmentChange() {
        this.isQualitativeExpanded =
            this.riskDetails.methodOfAssessment ===
            AssessmentMethodEnum.Qualitative;
        this.updateExposureLabel();
        this.setRiskRating();
    }

    setRiskRegisterControls() {
        const controls = this.riskDetails.riskRegisterControls as Control[];
        this.riskDetails.riskAssessmentControlIds =
            controls
                ?.filter(
                    (res) =>
                        RiskRegisterControlLinkType.RiskAssessment ===
                        res.riskRegisterControlLinkType,
                )
                .map((reg: Control) => reg.id);
        this.riskDetails.riskTreatmentControlIds =
            controls
                ?.filter(
                    (res) =>
                        RiskRegisterControlLinkType.RiskTreatment ===
                        res.riskRegisterControlLinkType,
                )
                .map((reg: Control) => reg.id);
        this.registerControlChange();
    }

    getOtherFields() {
        this.setLikelihoodScore();
        // DASH-2408: residual scores are independent of the initial assessment and
        // are no longer seeded from the qualitative initial values — they stay
        // blank/N/A until the user explicitly enters post-treatment values.
        this.setResidualLikelihoodScore();
        this.startDate = this.dateFormatService.convertTimestampToDate(
            this.riskDetails.periodStart,
        );
        this.endDate = this.dateFormatService.convertTimestampToDate(
            this.riskDetails.periodEnd,
        );
        this.riskStatus = isApprovedRiskRegisterStatus(this.riskDetails.assessmentStatus)
            ? "Approved"
            : "Draft";
        this.updateExposureLabel();
        this.loading = false;
    }

    private readBoolean(
        source: Record<string, unknown>,
        keys: string[],
        fallback = false,
    ): boolean {
        for (const key of keys) {
            const value = source[key];
            if (typeof value === "boolean") {
                return value;
            }
            if (typeof value === "string") {
                if (value.toLowerCase() === "true") return true;
                if (value.toLowerCase() === "false") return false;
            }
        }
        return fallback;
    }

    private toRecord(value: unknown): Record<string, unknown> {
        if (value && typeof value === "object") {
            return value as Record<string, unknown>;
        }

        return {};
    }

    private readNumber(
        source: Record<string, unknown>,
        keys: string[],
    ): number | null {
        for (const key of keys) {
            const value = source[key];
            if (typeof value === "number" && Number.isFinite(value)) {
                return value;
            }
            if (typeof value === "string") {
                const parsed = Number(value);
                if (Number.isFinite(parsed)) {
                    return parsed;
                }
            }
        }
        return null;
    }

    private readString(
        source: Record<string, unknown>,
        keys: string[],
        fallback = "",
    ): string {
        for (const key of keys) {
            const value = source[key];
            if (typeof value === "string" && value.trim()) {
                return value;
            }
        }
        return fallback;
    }

    private tryReadArray(
        source: Record<string, unknown>,
        keys: string[],
    ): unknown[] {
        for (const key of keys) {
            const value = source[key];
            if (Array.isArray(value)) {
                return value;
            }
        }
        return [];
    }

    private normalizeBayesianDistribution(
        raw: unknown[],
        fallbackCategories: Array<{ score: number; name: string }>,
    ): BayesianDistributionItem[] {
        if (!raw.length && fallbackCategories.length) {
            return fallbackCategories.map((item) => ({
                category: item.name,
                probability: 0,
                color: this.resolveBayesianChartColor(
                    this.likelihoodLevelColors.get(item.score),
                ),
                score: item.score,
            }));
        }

        const inferredAsFraction = (() => {
            const probabilities = raw
                .map((entry) => {
                    const item = entry as Record<string, unknown>;
                    return (
                        this.readNumber(item, [
                            "probability",
                            "value",
                            "percent",
                            "percentage",
                        ]) ?? 0
                    );
                })
                .filter((value) => Number.isFinite(value) && value >= 0);

            if (!probabilities.length) {
                return false;
            }

            const maxValue = Math.max(...probabilities);
            const totalValue = probabilities.reduce(
                (sum, value) => sum + value,
                0,
            );

            return maxValue <= 1 && totalValue <= 1.0001;
        })();

        return raw
            .map((entry) => {
                const item = entry as Record<string, unknown>;
                const rawLabel =
                    this.readString(item, ["category", "name", "label", "classification"]) ||
                    "";
                const score =
                    this.readNumber(item, ["score", "factor", "likelihoodScore", "key"]) ??
                    this.tryExtractNumericCategoryScore(rawLabel) ??
                    0;
                const probability =
                    this.readNumber(item, ["probability", "value", "percent", "percentage"]) ??
                    0;
                const probabilityPercent = inferredAsFraction
                    ? probability * 100
                    : probability;
                // DASH-2581: BE emits Label = band key/score, not a colour or name.
                // Join to the jurisdiction's likelihood levels for the display name and
                // colour, so the chart matches conventional Risk Assessment banding.
                const configuredLevel = this.likelihoodLevels.find(
                    (level) => level.score === score,
                );
                const color = this.resolveBayesianChartColor(
                    this.readString(item, ["color", "colour"]) ||
                        this.likelihoodLevelColors.get(score),
                );

                return {
                    category:
                        configuredLevel?.quantitativeName ||
                        configuredLevel?.name ||
                        rawLabel ||
                        `${score}`,
                    probability: Math.max(0, Math.min(100, probabilityPercent)),
                    color,
                    score,
                };
            })
            .sort((a, b) => a.score - b.score);
    }

    private tryExtractNumericCategoryScore(categoryLabel: string): number | null {
        if (!categoryLabel) {
            return null;
        }

        const leadingMatch = categoryLabel.match(/^\s*(-?\d+(?:\.\d+)?)/);
        if (leadingMatch) {
            const parsedLeading = Number(leadingMatch[1]);
            return Number.isFinite(parsedLeading) ? parsedLeading : null;
        }

        const firstNumericMatch = categoryLabel.match(/(-?\d+(?:\.\d+)?)/);
        if (!firstNumericMatch) {
            return null;
        }

        const parsedNumeric = Number(firstNumericMatch[1]);
        return Number.isFinite(parsedNumeric) ? parsedNumeric : null;
    }

    private resolveBayesianChartColor(color: string | undefined): string {
        if (!color) {
            return "#D1D5DB";
        }

        let normalizedColor = color.trim();

        // am5.color() can't parse CSS custom properties (DASH-2354) — resolve the
        // var() to its computed value first, or the column renders black.
        if (normalizedColor.startsWith("var(")) {
            const variableMatch = normalizedColor.match(/^var\((--[^)]+)\)$/);
            if (!variableMatch || typeof window === "undefined") {
                return "#D1D5DB";
            }
            normalizedColor = getComputedStyle(document.documentElement)
                .getPropertyValue(variableMatch[1])
                .trim();
        }

        if (!normalizedColor) {
            return "#D1D5DB";
        }

        // Stored band colours are sometimes persisted as bare hex without the leading
        // '#'; am5.color() then reads them as black. Restore the prefix.
        if (/^(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(normalizedColor)) {
            normalizedColor = `#${normalizedColor}`;
        }

        return normalizedColor;
    }

    // Full posterior stat table from a prior distribution's metric summaries
    // (GM/GSD/P95/exceedance, each with estimate/median/90% credible interval/UCL70/UCL90).
    private buildBayesianMetricRows(
        distribution: Record<string, unknown>,
    ): BayesianMetricRow[] {
        const unitSymbol =
            this.riskDetails?.agentUnits != null ? UnitSymbols[this.riskDetails.agentUnits] : "";
        const unitSuffix = unitSymbol ? ` (${unitSymbol})` : "";

        const concentration = (value: number | null): string =>
            value == null
                ? "-"
                : this.valueConversionService
                      .getParameterValueAsPerAgentUnit(value, this.riskDetails?.agentUnits)
                      .toFixed(2);
        const ratio = (value: number | null): string => (value == null ? "-" : value.toFixed(2));
        const percent = (value: number | null): string =>
            value == null ? "-" : `${(value * 100).toFixed(2)}%`;

        const specs: Array<{ key: string; label: string; format: (v: number | null) => string; emphasis?: boolean }> = [
            { key: "geometricMean", label: `${this.translateService.getDynamicTranslation("Geometric Mean")}${unitSuffix}`, format: concentration },
            { key: "geometricStandardDeviation", label: this.translateService.getDynamicTranslation("Geometric Standard Deviation"), format: ratio },
            // P95 drives the decision statistic, so it is emphasized in the table.
            { key: "percentile95", label: `${this.translateService.getDynamicTranslation("95th Percentile")}${unitSuffix}`, format: concentration, emphasis: true },
            { key: "exceedanceFraction", label: `${this.translateService.getDynamicTranslation("Exceedance Fraction")} (%)`, format: percent },
        ];

        return specs
            .map((spec) => {
                const summary = this.toRecord(distribution[spec.key]);
                if (!Object.keys(summary).length) {
                    return null;
                }
                const low = this.readNumber(summary, ["crLow"]);
                const high = this.readNumber(summary, ["crHigh"]);
                return {
                    label: spec.label,
                    tooltip: BAYESIAN_STAT_TOOLTIPS[spec.key] || "",
                    estimate: spec.format(this.readNumber(summary, ["mean"])),
                    median: spec.format(this.readNumber(summary, ["median"])),
                    credibleInterval:
                        low != null && high != null ? `${spec.format(low)} – ${spec.format(high)}` : "-",
                    ucl70: spec.format(this.readNumber(summary, ["ucl70"])),
                    ucl90: spec.format(this.readNumber(summary, ["ucl90"])),
                    emphasis: spec.emphasis === true,
                };
            })
            .filter((row): row is BayesianMetricRow => row !== null);
    }

    // Legend mapping each band's colour to its score, name and %OEL range — the charts
    // colour bars by band and the x-axis only shows the score.
    private buildBayesianBandLegend(): Array<{ color: string; label: string }> {
        return this.likelihoodLevels.map((level) => {
            const range =
                level.minQuantThreshold != null && level.maxQuantThreshold != null
                    ? ` (${level.minQuantThreshold}–${level.maxQuantThreshold}% OEL)`
                    : "";
            return {
                color: this.resolveBayesianChartColor(this.likelihoodLevelColors.get(level.score)),
                label: `${level.score} · ${level.quantitativeName || level.name}${range}`,
            };
        });
    }

    // Plain-language read-out of the posterior in terms of the exposure bands + key stats.
    private buildBayesianResultExplanation(
        distribution: Record<string, unknown>,
    ): { title: string; body: string } | null {
        const t = (key: string, params: Record<string, unknown> = {}): string =>
            this.translateService.getDynamicTranslation(key, params);
        const title = t("Estimated exposure rating");

        const bands = this.bayesianDistributionUninformative
            .filter((band) => band.probability > 0)
            .sort((a, b) => b.probability - a.probability);

        if (!bands.length) {
            return {
                title,
                body: t("Run the Bayesian analysis to estimate which exposure band applies."),
            };
        }

        const pct = (value: number): string => `${value.toFixed(1)}%`;
        const unitSymbol =
            this.riskDetails?.agentUnits != null ? UnitSymbols[this.riskDetails.agentUnits] : "";
        const p95Mean = this.readNumber(this.toRecord(distribution["percentile95"]), ["mean"]);
        const p95Text =
            p95Mean != null
                ? `${this.valueConversionService
                      .getParameterValueAsPerAgentUnit(p95Mean, this.riskDetails?.agentUnits)
                      .toFixed(2)}${unitSymbol ? ` ${unitSymbol}` : ""}`
                : "-";
        const exceedanceMean = this.readNumber(
            this.toRecord(distribution["exceedanceFraction"]),
            ["mean"],
        );
        const exceedanceText =
            exceedanceMean != null ? `${(exceedanceMean * 100).toFixed(2)}%` : "-";

        const [top, second] = bands;
        const body = second
            ? t(
                  "Based on the samples, there is a {{topProb}} probability the true exposure is in the {{topBand}} band ({{secondProb}} for {{secondBand}}). An estimated {{exceedance}} of exposures exceed the OEL, and the predicted 95th-percentile exposure is {{p95}}.",
                  {
                      topBand: top.category,
                      topProb: pct(top.probability),
                      secondBand: second.category,
                      secondProb: pct(second.probability),
                      exceedance: exceedanceText,
                      p95: p95Text,
                  },
              )
            : t(
                  "Based on the samples, there is a {{topProb}} probability the true exposure is in the {{topBand}} band. An estimated {{exceedance}} of exposures exceed the OEL, and the predicted 95th-percentile exposure is {{p95}}.",
                  {
                      topBand: top.category,
                      topProb: pct(top.probability),
                      exceedance: exceedanceText,
                      p95: p95Text,
                  },
              );

        return { title, body };
    }

    private buildBayesianSampleRows(): BayesianSampleRow[] {
        const samples = this.riskDetails?.riskRegisterSamples || [];
        return samples.map((sample) => {
            const censorType = sample.sampleCensorType;
            const censorSign =
                censorType != null
                    ? SampleCensorSign[censorType as SampleCensorType]
                    : SampleCensorSign[SampleCensorType.Equal];
            return {
                sampleId: String(sample.sampleId ?? "-"),
                sampleDate: sample.startDate ? new Date(sample.startDate * 1000) : null,
                sampleResult:
                    sample.value != null
                        ? `${censorSign} ${sample.value}${sample.unit ? ` ${sample.unit}` : ""}`
                        : "-",
                censoring: censorSign,
            };
        });
    }

    private updateBayesianDecisionState(): void {
        if (this.bayesianOelMissing || this.bayesianDecisionValue == null) {
            this.bayesianDecisionState = "unknown";
            this.bayesianDecisionStateLabel = "Unavailable";
            return;
        }

        const oelValue = this.riskDetails?.correctedOEL ?? this.riskDetails?.oel;
        if (oelValue == null) {
            this.bayesianDecisionState = "unknown";
            this.bayesianDecisionStateLabel = "Unavailable";
            return;
        }

        if (this.bayesianDecisionValue >= oelValue) {
            this.bayesianDecisionState = "above";
            this.bayesianDecisionStateLabel = "ABOVE OEL";
            return;
        }

        this.bayesianDecisionState = "below";
        this.bayesianDecisionStateLabel = "BELOW OEL";
    }

    private resolveBayesianFramework(): string {
        if (this.bayesianJurisdictionConfig.framework) {
            return this.bayesianJurisdictionConfig.framework;
        }

        const configRecord = this.toRecord(this.qualitativeConfig);
        if (configRecord) {
            const framework = this.readString(configRecord, [
                "bayesianFramework",
                "BayesianFramework",
            ]);
            if (framework) {
                return framework;
            }
        }

        return this.translateService.getDynamicTranslation("Bayesian Exposure Assessment");
    }

    private resolveDecisionStatisticLabel(): string {
        if (this.riskDetails?.quantitativeBDA) {
            return this.riskDetails.quantitativeBDA;
        }

        const bayesianDecisionStatistic = this.readNumber(
            this.toRecord(this.riskDetails),
            ["decisionStatistic", "DecisionStatistic", "bayesianDecisionStatistic"],
        );
        if (bayesianDecisionStatistic != null) {
            return this.quantitativeDecisionStatisticNames[bayesianDecisionStatistic] || "-";
        }

        const assessmentValue = this.readNumber(
            this.toRecord(this.initialRiskDetails),
            ["quantitativeDecisionStatistic"],
        );
        if (assessmentValue != null) {
            return this.quantitativeDecisionStatisticNames[assessmentValue] || "-";
        }

        return "-";
    }

    private buildComplianceDisplay(): void {
        const state =
            this.riskDetails?.complianceState ?? ComplianceStateEnum.NotAssessed;
        this.complianceDisplay = ComplianceStateDisplayMap[state] ?? null;
    }

    private buildDecisionNarrative(): void {
        const details = this.riskDetails;
        if (!details) {
            this.decisionNarrative = "";
            return;
        }

        const agentUnits = details.agentUnits;
        const rawDecision = details.quantitativeDecisionStatisticValue;
        const value =
            rawDecision == null
                ? "-"
                : agentUnits == null
                  ? Number(rawDecision.toFixed(4))
                  : parseFloat(
                        this.valueConversionService
                            .getParameterValueAsPerAgentUnit(rawDecision, agentUnits)
                            .toFixed(4),
                    );
        const params = {
            decisionStatistic: this.translateService.getDynamicTranslation(
                this.bayesianDecisionStatisticLabel,
            ),
            n: details.quantitativeNumberOfSamples ?? 0,
            requiredN: details.requiredNumberOfSamples ?? 0,
            distribution: details.wTestOutput ?? "-",
            value,
            unit: agentUnits != null ? UnitSymbols[agentUnits] : "-",
            percentOfOel:
                details.quantitativeResultPercentOfOEL != null
                    ? Number(details.quantitativeResultPercentOfOEL.toFixed(1))
                    : "-",
        };

        const state = details.complianceState ?? ComplianceStateEnum.NotAssessed;
        const basis = details.complianceBasis ?? null;

        // DASH-3163: EN 689 disabled for the jurisdiction — characterisation only, no verdict. The
        // required sample count now comes from the jurisdiction's configured exposure bands rather than
        // a single jurisdiction-wide number.
        if (state === ComplianceStateEnum.NotAssessed) {
            this.decisionNarrative = this.translateService.getDynamicTranslation(
                "Exposure characterised with {{n}} of {{requiredN}} samples: {{distribution}} distribution, {{decisionStatistic}} estimate {{value}} {{unit}} ({{percentOfOel}}% of the OEL). This jurisdiction is not configured for EN 689 compliance testing, so no compliance/non-compliance verdict is issued. The required sample count came from the jurisdiction's configured exposure bands.",
                params,
            );
            return;
        }

        const conclusion = this.translateService.getDynamicTranslation(
            ComplianceStateDisplayMap[state]?.label ?? "",
        );
        const reason = this.translateService.getDynamicTranslation(
            this.resolveComplianceReasonKey(state, basis),
        );

        // DASH-3218: an indeterminate verdict applied no EN 689 statistical test (basis NotAssessed while
        // the state is not NotAssessed), so it must characterise the exposure without claiming a test was
        // run — otherwise it falls through to the "Assessed under EN 689" wording below.
        if (basis === ComplianceBasisEnum.NotAssessed) {
            this.decisionNarrative = this.translateService.getDynamicTranslation(
                "Exposure characterised with {{n}} of {{requiredN}} samples: {{distribution}} distribution, {{decisionStatistic}} estimate {{value}} {{unit}} ({{percentOfOel}}% of the OEL). No EN 689 statistical test was applied. Result: {{conclusion}} — {{reason}}.",
                { ...params, conclusion, reason },
            );
            return;
        }

        // DASH-3163: the preliminary test (N = 3/4/5) reaches its verdict from the individual
        // measurements, not the decision statistic, so it gets its own narrative. The statistical
        // branch (N >= 6) keeps the original decision-statistic wording.
        if (basis === ComplianceBasisEnum.PreliminaryTest) {
            this.decisionNarrative = this.translateService.getDynamicTranslation(
                "Assessed under the EN 689 preliminary test with {{n}} of {{requiredN}} samples. Result: {{conclusion}} — {{reason}}.",
                { ...params, conclusion, reason },
            );
            return;
        }

        this.decisionNarrative = this.translateService.getDynamicTranslation(
            "Assessed under EN 689 ({{decisionStatistic}}) with {{n}} of {{requiredN}} required samples. The data best fit a {{distribution}} distribution. The decision statistic is {{value}} {{unit}}, or {{percentOfOel}}% of the OEL. Result: {{conclusion}} — {{reason}}.",
            { ...params, conclusion, reason },
        );
    }

    private resolveComplianceReasonKey(
        state: ComplianceStateEnum,
        basis: ComplianceBasisEnum | null,
    ): string {
        // DASH-3163: branch on ComplianceBasis so the reason states the actual criterion. The
        // preliminary test compares each individual measurement against the OEL fraction; the
        // statistical test compares the decision statistic against the OEL.
        if (basis === ComplianceBasisEnum.PreliminaryTest) {
            switch (state) {
                case ComplianceStateEnum.Compliant:
                    return "every measurement was below the applicable fraction of the occupational exposure limit, so the assessment is complete at this sample count";
                case ComplianceStateEnum.NonCompliant:
                    return "an individual measurement exceeded the occupational exposure limit";
                default:
                    return "";
            }
        }

        // DASH-3218: no statistical test concluded (basis NotAssessed), so the reason must not cite a
        // decision-statistic comparison the assessment never made.
        if (basis === ComplianceBasisEnum.NotAssessed) {
            switch (state) {
                case ComplianceStateEnum.Uncertain:
                    return "there were not enough valid samples to complete an EN 689 statistical test";
                case ComplianceStateEnum.NonCompliant:
                    return "the exposure was characterised above the occupational exposure limit";
                case ComplianceStateEnum.Compliant:
                    return "the exposure was characterised below the occupational exposure limit";
                default:
                    return "";
            }
        }

        switch (state) {
            case ComplianceStateEnum.Compliant:
                return "the decision statistic is below the occupational exposure limit";
            case ComplianceStateEnum.NonCompliant:
                return "the decision statistic exceeds the occupational exposure limit";
            case ComplianceStateEnum.Uncertain:
                return "there are insufficient samples to reach a compliance decision";
            default:
                return "";
        }
    }

    private prepareBayesianViewModel(): void {
        const riskRecord = this.toRecord(this.riskDetails);
        const jurisdictionDefaultPriorMode = this.bayesianJurisdictionConfig.defaultPriorMode ?? 0;
        const jurisdictionDefaultInformative = jurisdictionDefaultPriorMode === 1;
        const defaultPriorInformative = this.readBoolean(
            riskRecord,
            [
                "bayesianDefaultPriorIsInformative",
                "bayesianDefaultInformativePrior",
                "defaultBayesianPriorIsInformative",
            ],
            jurisdictionDefaultInformative,
        );
        this.bayesianDefaultPriorIsInformative = defaultPriorInformative;

        this.bayesianPriorPayloadKey = [
            "bayesianUseInformativePrior",
            "useInformativePrior",
            "activeInformativePrior",
        ].find((key) => key in riskRecord) || "bayesianUseInformativePrior";

        // Fix 2: don't clobber an in-session user selection when an async hydrate or a
        // completed run re-runs this. When not dirty, prefer the actually-run priorMode,
        // falling back to the jurisdiction default.
        const runPriorMode = this.readNumber(riskRecord, ["priorMode", "PriorMode"]);
        const priorModeInformative =
            runPriorMode != null ? runPriorMode === 1 : defaultPriorInformative;
        if (!this.bayesianPriorSelectionDirty) {
            this.bayesianUseInformativePrior = this.readBoolean(
                riskRecord,
                [this.bayesianPriorPayloadKey],
                priorModeInformative,
            );
        }

        const priorAuditFromApi = this.readString(riskRecord, [
            "bayesianPriorOverrideAuditNote",
            "bayesianPriorAuditNote",
        ]);
        this.bayesianPriorOverrideAuditNote =
            priorAuditFromApi ||
            (this.bayesianUseInformativePrior !== this.bayesianDefaultPriorIsInformative
                ? "Assessment prior was overridden from jurisdiction default"
                : "");

        const fallbackCategories = this.likelihoodLevels.map((level) => ({
            score: level.score,
            name: level.name,
        }));

        // DASH-2581: prefer the dual prior distributions the BE now returns — each a
        // nested object carrying its own categoryProbabilities + GM/GSD. Fall back to
        // the legacy grouped/flat shapes for results stored before the contract landed.
        const uninformativeDistribution = this.toRecord(
            riskRecord["uninformativeDistribution"] ?? riskRecord["UninformativeDistribution"],
        );
        const informativeDistribution = this.toRecord(
            riskRecord["informativeDistribution"] ?? riskRecord["InformativeDistribution"],
        );

        const groupedDistribution =
            (riskRecord["bayesianCategoryProbabilityDistribution"] as Record<string, unknown>) ||
            null;

        let uninformativeRaw = this.tryReadArray(uninformativeDistribution, [
            "categoryProbabilities",
            "CategoryProbabilities",
        ]);
        if (!uninformativeRaw.length) {
            uninformativeRaw = groupedDistribution
                ? this.tryReadArray(groupedDistribution, ["uninformative", "Uninformative"])
                : this.tryReadArray(riskRecord, ["bayesianUninformativeDistribution"]);
        }

        let informativeRaw = this.tryReadArray(informativeDistribution, [
            "categoryProbabilities",
            "CategoryProbabilities",
        ]);
        if (!informativeRaw.length) {
            informativeRaw = groupedDistribution
                ? this.tryReadArray(groupedDistribution, ["informative", "Informative", "qra"])
                : this.tryReadArray(riskRecord, ["bayesianInformativeDistribution", "qraDistribution"]);
        }

        // Legacy single-array fallback: results stored before dual distributions
        // exposed only one CategoryProbabilities collection for the active prior.
        if (!uninformativeRaw.length && !informativeRaw.length) {
            const singlePriorDistribution = this.tryReadArray(riskRecord, [
                "categoryProbabilities",
                "CategoryProbabilities",
            ]);
            if (singlePriorDistribution.length) {
                if (runPriorMode === 1) {
                    informativeRaw = singlePriorDistribution;
                } else {
                    uninformativeRaw = singlePriorDistribution;
                }
            }
        }

        this.bayesianDistributionUninformative = this.normalizeBayesianDistribution(
            uninformativeRaw,
            fallbackCategories,
        );
        this.bayesianDistributionInformative = this.normalizeBayesianDistribution(
            informativeRaw,
            fallbackCategories,
        );

        // Fix 1: drive the no-QRA fallback off hasInformativePrior from the response
        // when present; otherwise infer it from an empty informative distribution.
        const hasInformativePriorKey = ["hasInformativePrior", "HasInformativePrior"].find(
            (key) => key in riskRecord,
        );
        const informativeProbabilityTotal = this.bayesianDistributionInformative.reduce(
            (total, item) => total + item.probability,
            0,
        );
        this.bayesianNoQraFallback = hasInformativePriorKey
            ? !this.readBoolean(riskRecord, [hasInformativePriorKey], true)
            : informativeRaw.length === 0 || informativeProbabilityTotal <= 0;
        if (this.bayesianNoQraFallback) {
            this.bayesianUseInformativePrior = false;
        }

        this.bayesianDecisionValue = this.readNumber(riskRecord, [
            "bayesianDecisionValue",
            "bayesianDecisionStatisticValue",
            "decisionValue",
            "DecisionValue",
            "quantitativeDecisionStatisticValue",
        ]);
        this.bayesianConvertedDecisionValue =
            this.bayesianDecisionValue != null
                ? `${this.valueConversionService
                      .getParameterValueAsPerAgentUnit(
                          this.bayesianDecisionValue,
                          this.riskDetails?.agentUnits,
                      )
                      .toFixed(4)} ${
                      this.riskDetails?.agentUnits != null
                          ? UnitSymbols[this.riskDetails.agentUnits]
                          : "-"
                  }`
                : "-";

        this.bayesianFrameworkLabel = this.resolveBayesianFramework();
        this.bayesianDecisionStatisticLabel = this.resolveDecisionStatisticLabel();
        this.buildComplianceDisplay();
        this.buildDecisionNarrative();

        const hasOelApiKey = ["hasOel", "HasOel"].find((key) => key in riskRecord);
        const hasOelFromApi = hasOelApiKey
            ? this.readBoolean(riskRecord, [hasOelApiKey], false)
            : null;
        this.bayesianOelMissing = hasOelFromApi === false || !Boolean(this.riskDetails?.oel);
        this.bayesianNoOelReason = this.readString(riskRecord, [
            "noOelReason",
            "NoOelReason",
            "bayesianNoOelReason",
        ]);

        // BE PR893 integration point: if stale flag is returned from a read endpoint,
        // FE surfaces a guardrail banner. A rerun trigger is still BE/endpoint-dependent.
        this.bayesianResultStale = this.readBoolean(
            riskRecord,
            ["bayesianIsStale", "isBayesianResultStale", "isStale", "IsStale"],
            false,
        );

        this.bayesianSamplesUsed = this.buildBayesianSampleRows();
        this.bayesianSamplesMissing = this.bayesianSamplesUsed.length === 0;
        this.bayesianInsufficientSamples =
            this.bayesianSamplesUsed.length > 0 && this.bayesianSamplesUsed.length < 3;
        this.bayesianCensoringGuardrail =
            this.bayesianJurisdictionConfig.enableCensoringGuardrail !== false &&
            (
                this.readBoolean(riskRecord, [
                    "isHighCensoringFraction",
                    "IsHighCensoringFraction",
                ], false) ||
                (this.riskDetails?.riskRegisterSamples?.some(
                    (sample) => sample.sampleCensorType === SampleCensorType.LessThan,
                ) ?? false)
            );

        this.bayesianUninformativeMetrics = this.buildBayesianMetricRows(uninformativeDistribution);
        this.bayesianBandLegend = this.buildBayesianBandLegend();
        if (this.bayesianInformativeDisabled) {
            this.bayesianUseInformativePrior = false;
        }
        this.updateBayesianDecisionState();
        this.bayesianResultExplanation = this.buildBayesianResultExplanation(uninformativeDistribution);
        this.updateBayesianRunButtonLabel();

        if (this.selectedRiskTab === 1 && this.bayesianEnabled) {
            this.scheduleBayesianChartRender();
        }
    }

    toggleBayesianPrior(shouldUseInformative: boolean): void {
        this.bayesianPriorSelectionDirty = true;
        if (shouldUseInformative && this.bayesianNoQraFallback) {
            this.bayesianUseInformativePrior = false;
            return;
        }

        this.bayesianUseInformativePrior = shouldUseInformative;
        this.bayesianPriorOverrideAuditNote =
            this.bayesianUseInformativePrior !== this.bayesianDefaultPriorIsInformative
                ? "Assessment prior was overridden from jurisdiction default"
                : "";
    }

    // Fix 3: the chart <div> is created by the tab body only after the tab-change
    // render, so a synchronous render fired from the tab event (or a hydrate that
    // resolves before the tab is drawn) finds no host element. Defer to the next
    // render so #risk-register-bayesian-distribution-chart exists.
    private scheduleBayesianChartRender(): void {
        if (!this.bayesianEnabled) {
            return;
        }
        afterNextRender(() => void this.renderBayesianDistributionChart(), {
            injector: this.injector,
        });
    }

    // AIHA IHDataAnalyst-style panels: Prior / Likelihood / Posterior for each prior
    // mode. The BE returns only the posterior per mode, so the rest is derived:
    // - Likelihood == uninformative posterior (a uniform prior leaves the likelihood
    //   unchanged, so posterior ∝ likelihood), and is shared by both modes.
    // - Uninformative prior is uniform (1/N).
    // - Informative prior is recovered by Bayes back-out: prior ∝ posterior / likelihood,
    //   normalised. Accurate because the QRA prior is piecewise-constant across the bins.
    private buildBayesianPanels(): Record<BayesianPanelKey, BayesianPanelRow[]> {
        const categories = this.bayesianDistributionUninformative;
        const informativeByScore = new Map(
            this.bayesianDistributionInformative.map((item) => [item.score, item.probability]),
        );
        const count = categories.length || 1;

        const likelihood = categories.map((item) => item.probability / 100);
        const informativePosterior = categories.map(
            (item) => (informativeByScore.get(item.score) ?? 0) / 100,
        );

        const rawInformativePrior = categories.map((_item, index) =>
            likelihood[index] > BAYESIAN_LIKELIHOOD_EPSILON
                ? informativePosterior[index] / likelihood[index]
                : 0,
        );
        const informativePriorSum = rawInformativePrior.reduce((sum, value) => sum + value, 0);
        const informativePrior = rawInformativePrior.map((value) =>
            informativePriorSum > 0 ? value / informativePriorSum : 0,
        );

        const toRows = (values: number[]): BayesianPanelRow[] =>
            categories.map((item, index) => ({
                rating: `${item.score}`,
                value: values[index],
                color: item.color,
            }));

        return {
            uninformativePrior: toRows(categories.map(() => 1 / count)),
            uninformativeLikelihood: toRows(likelihood),
            uninformativePosterior: toRows(likelihood),
            informativePrior: toRows(informativePrior),
            informativeLikelihood: toRows(likelihood),
            informativePosterior: toRows(informativePosterior),
        };
    }

    private async renderBayesianDistributionChart(): Promise<void> {
        if (!this.bayesianEnabled) {
            return;
        }

        this.disposeBayesianCharts();

        const panels = this.buildBayesianPanels();
        // Nothing meaningful to draw before a run resolves posterior data.
        const hasPosterior =
            panels.uninformativeLikelihood.some((row) => row.value > 0) ||
            panels.informativePosterior.some((row) => row.value > 0);
        if (!hasPosterior) {
            return;
        }

        const { am5: am5Ns, am5xy: am5xyNs } = await this.chartService.getAmCharts5();
        (Object.keys(panels) as BayesianPanelKey[]).forEach((key) => {
            // Isolate each panel so a synchronous setup failure in one still leaves the
            // others drawn. (Formatter errors fire later on the amCharts ticker and are
            // prevented by cleanBayesianChartValue, not by this catch.)
            try {
                const root = this.renderBayesianPanel(
                    am5Ns,
                    am5xyNs,
                    this.bayesianPanelIds[key],
                    panels[key],
                );
                if (root) {
                    this.bayesianRoots.push(root);
                }
            } catch (error) {
                Sentry.captureException(error, {
                    extra: { riskRegisterId: this.id, bayesianPanel: key },
                });
            }
        });
    }

    // Collapse near-zero probabilities so amCharts never derives a >100-digit toFixed()
    // call from a tiny exponent (e.g. 4.15e-94). Rounding to 6 decimals maps anything
    // below 5e-7 to exactly 0, leaving the formatter with 0 or a value of at least 1e-6.
    private cleanBayesianChartValue(value: number): number {
        if (!Number.isFinite(value)) {
            return 0;
        }
        const factor = 10 ** BAYESIAN_CHART_VALUE_DECIMALS;
        return Math.round(value * factor) / factor;
    }

    private renderBayesianPanel(
        am5Ns: typeof import("@amcharts/amcharts5"),
        am5xyNs: typeof import("@amcharts/amcharts5/xy"),
        elementId: string,
        rows: BayesianPanelRow[],
    ): am5.Root | null {
        if (typeof document === "undefined" || !document.getElementById(elementId) || !rows.length) {
            return null;
        }

        // Feed the chart cleaned values only: the tooltip and bullet label both resolve
        // {valueY.formatNumber(...)} against this data, so rounding here keeps every
        // formatter safe. The unrounded probabilities remain in the stats table and the
        // decision narrative, which are built independently of these panel rows.
        const chartRows: BayesianPanelRow[] = rows.map((row) => ({
            ...row,
            value: this.cleanBayesianChartValue(row.value),
        }));

        const root = am5Ns.Root.new(elementId);
        try {
            root.setThemes([]);
            root._logo?.dispose();

            const chart = root.container.children.push(am5xyNs.XYChart.new(root, {
                paddingLeft: 2,
                paddingRight: 2,
                paddingTop: 26,
                paddingBottom: 2,
            }));

            const categoryAxis = chart.xAxes.push(am5xyNs.CategoryAxis.new(root, {
                categoryField: "rating",
                renderer: am5xyNs.AxisRendererX.new(root, { minGridDistance: 10 }),
            }));
            categoryAxis.get("renderer").grid.template.set("visible", false);
            categoryAxis.get("renderer").labels.template.set("fontSize", 10);
            categoryAxis.data.setAll(chartRows);

            const valueAxis = chart.yAxes.push(am5xyNs.ValueAxis.new(root, {
                min: 0,
                max: 1,
                strictMinMax: true,
                renderer: am5xyNs.AxisRendererY.new(root, { minGridDistance: 24 }),
            }));
            valueAxis.get("renderer").labels.template.set("fontSize", 10);

            const series = chart.series.push(am5xyNs.ColumnSeries.new(root, {
                xAxis: categoryAxis,
                yAxis: valueAxis,
                valueYField: "value",
                categoryXField: "rating",
            }));
            series.columns.template.setAll({
                width: am5Ns.percent(62),
                strokeWidth: 0,
                tooltipText: "{categoryX}: [bold]{valueY.formatNumber('#.###')}[/]",
            });
            series.columns.template.adapters.add("fill", (fill, target) => {
                const row = target.dataItem?.dataContext as BayesianPanelRow | undefined;
                return row?.color ? am5Ns.color(row.color) : fill;
            });

            // v5 replacement for v4's LabelBullet: a Label sprite as the bullet, with
            // populateText so `{valueY...}` re-resolves against each column's data item.
            // locationY 0 anchors the bullet at the bar's value end (top); dy lifts the
            // label above the bar instead of sitting inside it.
            series.bullets.push(() => am5Ns.Bullet.new(root, {
                locationY: 0,
                sprite: am5Ns.Label.new(root, {
                    text: "{valueY.formatNumber('#.###')}",
                    populateText: true,
                    fontSize: 9,
                    dy: -6,
                    centerX: am5Ns.p50,
                    centerY: am5Ns.p100,
                }),
            }));

            series.data.setAll(chartRows);

            return root;
        } catch (error) {
            // A root that fails mid-setup never reaches bayesianRoots, so
            // disposeBayesianCharts() can't clean it up. Dispose it here before
            // rethrowing, otherwise its ticker keeps running and the next
            // Root.new on this element is rejected (duplicate root).
            root.dispose();
            throw error;
        }
    }

    private disposeBayesianCharts(): void {
        this.bayesianRoots.forEach((root) => root.dispose());
        this.bayesianRoots = [];
    }

    save() {
        // When the parent RA is Approved every register write path is locked (DASH-3217 Part C),
        // including the status selector — the RA must be un-approved first — so block every save.
        if (this.isParentAssessmentApproved) {
            return;
        }

        // Approved records are read-only except for reverting the status selector back to
        // Draft (per design). If it's still Approved, the fields stayed locked in the UI, but
        // a client that bypasses the [disabled] bindings must not be able to sneak a save through.
        if (this.isApproved && this.riskStatus === "Approved") {
            return;
        }

        const isValid = this.onConsequenceRatingChange(true);
        if (!isValid) return;

        if (this.consequenceRatingChanged && !this.riskDetails.consequenceRatingComment?.trim()) {
            this.toastService.displayErrorMessage("Consequence Rating Comment is required when changing the value", true);
            return;
        }

        if (!this.isApproved && this.isQualitativeCommentMissing) {
            this.toastService.displayErrorMessage("A comment is required for qualitative assessments", true);
            return;
        }

        if (!this.isApproved && this.isQualitativeExposureScoreMissing) {
            this.toastService.displayErrorMessage(
                "{{field}} is required for qualitative assessments",
                true,
                { field: this.translateService.getDynamicTranslation(this.exceedanceProbabilityLabel) },
            );
            return;
        }

        if (!this.isApproved && this.isQualitativeFrequencyScoreMissing) {
            this.toastService.displayErrorMessage(
                "{{field}} is required for qualitative assessments",
                true,
                { field: this.translateService.getDynamicTranslation(this.frequencyExposureLabel) },
            );
            return;
        }

        this.loading = true;
        this.riskDetails.assessmentStatus =
            RiskRegisterAssessmentStatusEnum[this.riskStatus];
        const controls = {
            riskRegisterControls: [
                ...this.riskDetails.riskRegisterControls.map((res) => ({
                    controlId: res.id,
                    riskRegisterControlLinkType:
                        res.riskRegisterControlLinkType,
                })),
            ],
        };
        // healthRiskScore is round-tripped UNCHANGED as loaded from the server — deliberately not
        // blanked out here. AC5 forbids sending a score the BROWSER computed, and nothing in this
        // component writes riskDetails.healthRiskScore any more (it is read-only, see setRiskRating).
        //
        // Blanking it broke psychological hazards: the field would be absent from the JSON, deserialize
        // as null, and CrudService.Update maps the DTO onto the entity BEFORE OnBeforeUpdate — where
        // ClientScoreShouldBeStripped() exempts psychological agents, so the null was never restored and
        // the user-entered Risk Scoring value was silently wiped by any save that did not change
        // likelihood/consequence/method. For every other agent type the backend strips the incoming
        // value regardless, so round-tripping it is a no-op there.
        const payload = {
            ...this.riskDetails,
            ...controls,
            [this.bayesianPriorPayloadKey]: this.bayesianUseInformativePrior,
            bayesianPriorOverrideAuditNote: this.bayesianPriorOverrideAuditNote || null,
            oelCorrectionParams: this.riskDetails?.oelCorrectionParams
                ? JSON.stringify({
                      shiftLengthHours:
                          this.riskDetails.oelCorrectionParams.shiftLengthHours,
                      weeklyHours:
                          this.riskDetails.oelCorrectionParams.weeklyHours,
                      pressureMmHg:
                          this.riskDetails.oelCorrectionParams.pressureMmHg,
                  })
                : null,
        };
        this.riskRegisterService
            .update(payload as RiskRegister)
            .subscribe({
                next: () => {
                    this.loadRiskDetails();
                    this.updateGridEmit.emit();
                    this.toastService.showSuccess("Updated successfully", true);
                },
                error: (error) => {
                    this.loading = false;
                    this.toastService.showError(error);
                },
            });
    }

    closePopup() {
        this.show = false;
        this.editDateRange = false;
        this.hideRiskDetailEmit.emit("sample event");
    }

    // DASH-2331: resolve the qualitative likelihood score for a computed product
    // (exposure × frequency). The Qualitative Threshold ranges configured on the
    // jurisdiction's likelihood levels (QualThresholds, parsed into
    // likelihoodFactorsByRange) are the source of truth the Risk Configuration form
    // edits, so they take precedence — that way an updated threshold range is
    // re-evaluated here instead of using the stale qualitative likelihoodMapping
    // product ranges. Fall back to the likelihoodMapping, then the hard-coded
    // defaults, so jurisdictions without explicit thresholds still resolve.
    private resolveLikelihoodFromProduct(
        product: number,
    ): { score: number; text: string } | null {
        for (const [rangeKey, scoreKey] of this.likelihoodFactorsByRange) {
            const [min, max] = rangeKey.split(":").map(Number);
            if (
                Number.isFinite(min) &&
                Number.isFinite(max) &&
                product >= min &&
                product <= max
            ) {
                const score = Number(scoreKey);
                const level = this.likelihoodLevels.find(
                    (item) => item.score === score,
                );
                return { score, text: level?.name || "" };
            }
        }

        const mappingToUse =
            this.likelihoodMapping && this.likelihoodMapping.length > 0
                ? this.likelihoodMapping
                : (QualitativeMetrics as LikelihoodMapping[]);
        const match = mappingToUse.find(
            (res) =>
                product >= res.exposureFrequencyScore[0] &&
                product <= res.exposureFrequencyScore[1],
        );
        if (match) {
            return { score: match.likelihoodScore, text: match.classification };
        }

        return null;
    }

    setLikelihoodScore() {
        const value =
            this.riskDetails.qualitativeExposureScore *
            this.riskDetails.qualitativeFrequencyScore;
        this.qualitativeLikelihoodText = "";
        const resolved = this.resolveLikelihoodFromProduct(value);
        if (resolved) {
            this.riskDetails.qualitativeLikelihoodScore = resolved.score;
            // DASH-2278 #11: show the likelihood LEVEL NAME only, not the factor.
            this.qualitativeLikelihoodText = resolved.text;
        }
        this.updateExposureLabel();
        this.riskStatus = "Draft";
        this.setRiskRating();
    }

    setRiskRating() {
        // Criterion 2: resolve Risk Rating from the persisted healthRiskScore.
        // Null score → neutral grey (criterion 4), never a colour derived from 0.
        const score = this.riskDetails?.healthRiskScore;
        this.riskRating = score != null ? Math.round(score) : 0;
        this.riskRatingColor = score != null
            ? getRiskColor(Math.round(score), this.riskLevels)
            : this.commonService.colors.greyLight;
        this.riskRatingName = score != null
            ? getRiskLevelName(Math.round(score), this.riskLevels)
            : null;
        this.buildMatrixData();
    }

    private resetConsequenceRatingToOriginal(): void {
        this.riskDetails.consequenceRating = this.originalConsequenceRating;
        this.consequenceRatingChanged = false;
        this.riskDetails.consequenceRatingComment = this.originalConsequenceRatingComment;
        this.updateHasConsequenceRatingComment(this.riskDetails.consequenceRatingComment);
        this.setRiskRating();
        this.setResidualRiskRating();
    }

    onConsequenceRatingChange(shouldValidate = false): boolean {
        const currentValue = this.riskDetails.consequenceRating;

        if (currentValue == null) {
            if (!shouldValidate) return true;

            this.resetConsequenceRatingToOriginal();
            return true;
        }

        // Dropdown restricts to valid values only, so no range validation needed

        this.consequenceRatingChanged =
            this.originalConsequenceRating !== null &&
            this.riskDetails.consequenceRating !== this.originalConsequenceRating
        if (this.consequenceRatingChanged) {
            this.isConsequenceRatingCommentExpanded = true;
        } else {
            // Reverted to original base value — restore the pre-edit comment state
            this.riskDetails.consequenceRatingComment = this.originalConsequenceRatingComment;
            this.updateHasConsequenceRatingComment(
                this.riskDetails.consequenceRatingComment,
            );
        }
        this.setRiskRating();
        this.setResidualRiskRating();
        return true;
    }

    updateHasConsequenceRatingComment(comment: string | null): void {
        this.hasConsequenceRatingComment = !!comment?.trim();
    }

    get isConsequenceRatingCommentOpen(): boolean {
        return this.isConsequenceRatingCommentExpanded;
    }

    toggleConsequenceRatingComment(): void {
        this.isConsequenceRatingCommentExpanded = !this.isConsequenceRatingCommentExpanded;
    }

    get isApproved(): boolean {
        return (
            this.isParentAssessmentApproved ||
            isApprovedRiskRegisterStatus(this.riskDetails?.assessmentStatus)
        );
    }

    recalculateAssessment() {
        if (
            !this.canRecalculate ||
            this.loading ||
            !this.initialRiskDetails?.riskAssessmentId
        ) {
            return;
        }
        this.loading = true;
        // Source the payload from initialRiskDetails (the load-time server snapshot),
        // never this.riskDetails which may hold unsaved user edits: a recalculation must
        // persist the register's current values with no field changes. Update force-marks
        // every column modified, so the full persisted record is sent — a stripped
        // id-only payload would blank the fields it omits. oelCorrectionParams and
        // riskRegisterControls are reshaped exactly as save() serializes them.
        const persisted = this.initialRiskDetails;
        const controls = {
            riskRegisterControls: [
                ...(persisted.riskRegisterControls ?? []).map((res) => ({
                    controlId: res.id,
                    riskRegisterControlLinkType: res.riskRegisterControlLinkType,
                })),
            ],
        };
        const payload = {
            ...persisted,
            ...controls,
            oelCorrectionParams: persisted.oelCorrectionParams
                ? JSON.stringify({
                      shiftLengthHours:
                          persisted.oelCorrectionParams.shiftLengthHours,
                      weeklyHours: persisted.oelCorrectionParams.weeklyHours,
                      pressureMmHg: persisted.oelCorrectionParams.pressureMmHg,
                  })
                : null,
        };
        this.riskRegisterService
            .update(payload as RiskRegister)
            .subscribe({
                next: () => {
                    this.loadRiskDetails();
                    this.updateGridEmit.emit(true);
                    this.toastService.showSuccess(
                        "Assessment recalculated",
                        true,
                    );
                },
                error: (error) => {
                    this.loading = false;
                    this.toastService.showError(error);
                },
            });
    }

    get isQualitativeAssessment(): boolean {
        return (
            this.riskDetails?.methodOfAssessment ===
            AssessmentMethodEnum.Qualitative
        );
    }

    get isQualitativeCommentMissing(): boolean {
        return this.isQualitativeAssessment && !this.riskDetails?.note?.trim();
    }

    get isQualitativeExposureScoreMissing(): boolean {
        return (
            this.isQualitativeAssessment &&
            !this.riskDetails?.qualitativeExposureScore
        );
    }

    get isQualitativeFrequencyScoreMissing(): boolean {
        return (
            this.isQualitativeAssessment &&
            !this.riskDetails?.qualitativeFrequencyScore
        );
    }

    get isQualitativeScoreMissing(): boolean {
        return (
            this.isQualitativeExposureScoreMissing ||
            this.isQualitativeFrequencyScoreMissing
        );
    }

    setResidualLikelihoodScore() {
        // DASH-2408: the residual (post-treatment) likelihood is independent of the
        // initial assessment. Only derive it once the user has explicitly entered
        // BOTH residual exposure and frequency; until then leave the likelihood and
        // health-risk rating unset so they read as blank/N/A rather than mirroring
        // the initial values.
        const exposure = this.riskDetails.residualExposureScore;
        const frequency = this.riskDetails.residualFrequencyScore;
        this.residualLikelihoodText = "";
        if (!exposure || exposure <= 0 || !frequency || frequency <= 0) {
            this.riskDetails.residualLikelihoodScore = null;
            this.setResidualRiskRating();
            return;
        }
        const resolved = this.resolveLikelihoodFromProduct(exposure * frequency);
        if (resolved) {
            this.riskDetails.residualLikelihoodScore = resolved.score;
            this.residualLikelihoodText = resolved.text;
        } else {
            this.riskDetails.residualLikelihoodScore = null;
        }
        this.setResidualRiskRating();
    }

    setResidualRiskRating() {
        // Criterion 3: resolve residual rating from the persisted residualHealthRisk.
        // Null score → neutral grey (criterion 4), never a colour derived from 0.
        const score = this.riskDetails?.residualHealthRisk;
        this.riskResidualRating = score != null ? Math.round(score) : 0;
        this.riskResidualRatingColor = score != null
            ? getRiskColor(Math.round(score), this.riskLevels)
            : this.commonService.colors.greyLight;
    }

    registerControlChange() {
        const ids = [
            ...this.riskDetails.riskAssessmentControlIds,
            ...this.riskDetails.riskTreatmentControlIds,
        ];
        if (ids.length) {
            const filterBy = {
                OperatorTypeEnum: JSON.stringify({
                    Condition: OperatorTypeEnum.Equal.toString(),
                    Field: "id",
                    Value: ids.join(","),
                }),
            };

            const filterBySeg = {
                OperatorTypeEnum: JSON.stringify({
                    Condition: OperatorTypeEnum.Equal.toString(),
                    Field: "controlId",
                    Value: ids.join(","),
                }),
                organizationLevelId: this.riskDetails.organizationLevelId,
                segId: this.riskDetails.segId,
            };

            forkJoin({
                controls: this.controlService.get(
                    "",
                    "id",
                    "asc",
                    0,
                    ids.length,
                    JSON.stringify(filterBy),
                ),
                segs: this.controlOrgLevelSegService.get(
                    "",
                    "",
                    "asc",
                    0,
                    ids.length,
                    JSON.stringify(filterBySeg),
                ),
            }).subscribe({
                next: ({ controls, segs }) => {
                    this.riskDetails.riskRegisterControls = controls.data.map(
                        (control) => {
                            const segOrg = segs.data.find(
                                (seg) => seg.controlId === control.id,
                            );
                            return {
                                ...control,
                                riskRegisterControlLinkType:
                                    this.riskDetails.riskAssessmentControlIds.includes(
                                        control.id,
                                    )
                                        ? RiskRegisterControlLinkType.RiskAssessment
                                        : RiskRegisterControlLinkType.RiskTreatment,
                                segOrgStatus:
                                    segOrg?.status != null
                                        ? ControlStatusEnum[segOrg.status]
                                        : "-",
                                targetImplementationEnd:
                                    segOrg?.targetImplementationEnd,
                            };
                        },
                    );

                    this.updateRiskTableData();
                },
                error: (err) => this.toastService.showError(err),
            });
        }
    }

    linkControlsDialog(isAssessment = false) {
        const mapped =
            this.riskDetails.riskRegisterControls?.map((r) => r.id) ?? [];
        const ids = [
            ...this.riskDetails.riskAssessmentControlIds,
            ...this.riskDetails.riskTreatmentControlIds,
        ];
        const duplicateId = ids.find((id, index) => ids.indexOf(id) !== index);
        if (duplicateId) {
            if (isAssessment) {
                this.riskDetails.riskAssessmentControlIds =
                    this.riskDetails.riskAssessmentControlIds.filter(
                        (id) => id !== duplicateId,
                    );
            } else {
                this.riskDetails.riskTreatmentControlIds =
                    this.riskDetails.riskTreatmentControlIds.filter(
                        (id) => id !== duplicateId,
                    );
            }
            this.toastService.displayErrorMessage("Control is already added in existing controls", true);
            return;
        }
        const missing = ids.find((id) => !mapped.includes(id));
        if (!missing) {
            this.riskDetails.riskRegisterControls =
                (this.riskDetails.riskRegisterControls as Control[]).filter((res) =>
                    ids.includes(res.id),
                );
            this.updateRiskTableData();
            return;
        }
        const dialogRef = this.dialog.open(LinkControlDialogComponent, {
            data: {
                id: missing,
                riskDetails: this.riskDetails,
                isAssessment: isAssessment,
            },
            panelClass: "link-control-view-dialog",
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                this.riskDetails.riskAssessmentControlIds =
                    this.riskDetails.riskAssessmentControlIds.filter(
                        (id) => id !== missing,
                    );
                this.riskDetails.riskTreatmentControlIds =
                    this.riskDetails.riskTreatmentControlIds.filter(
                        (id) => id !== missing,
                    );
                return;
            }
            this.registerControlChange();
        });
    }

    openSampleSelector() {
        const nonOutlierSamples = this.riskDetails.riskRegisterSamples ?? [];
        const outlierSamples = (this.riskDetails.outlierSamples ?? []).map(
            (sample) => ({
                ...sample,
                isOutlier: true,
            }),
        );
        const hasSamples =
            this.riskDetails.quantitativeNumberOfSamples >= 1 ||
            outlierSamples.length > 0;

        if (hasSamples) {
            const sampleMap = new Map<string, RiskRegisterSample>();
            [...nonOutlierSamples, ...outlierSamples].forEach((sample) => {
                const key = sample.sampleId;
                const existing = sampleMap.get(key);
                if (!existing || sample.isOutlier) {
                    sampleMap.set(key, sample);
                }
            });
            const dialogRef = this.dialog.open(SampleGridViewDialogComponent, {
                data: {
                    id: this.riskDetails.id,
                    riskAssessmentId: this.riskDetails.riskAssessmentId,
                    riskRegisterSamples: Array.from(sampleMap.values()),
                    readOnly: this.isApproved,
                },
                panelClass: "sample-grid-view-dialog",
            });

            dialogRef.afterClosed().subscribe((result) => {
                if (result === true) {
                    this.loadRiskDetails();
                    this.updateGridEmit.emit(true);
                }
            });
        }
    }

    private formatReportedDisplay(sample: RiskRegisterSample): string {
        const censorSign =
            SampleCensorSign[sample.sampleCensorType as SampleCensorType] ??
            SampleCensorSign[SampleCensorType.Equal];
        const unitSuffix = sample.unit ? ` ${sample.unit}` : "";
        return `${censorSign} ${sample.value ?? "-"}${unitSuffix}`.trim();
    }

    dateChange(dateType: "start" | "end") {
        if (dateType === "start") {
            this.riskDetails.periodStart =
                this.dateFormatService.convertDateToTimestamp(this.startDate);
        } else {
            this.riskDetails.periodEnd =
                this.dateFormatService.convertDateToTimestamp(this.endDate);
        }
    }

    // Maps risk-register samples to chart rows sorted ascending by start date.
    // A DateAxis drops items that arrive out of date order, so the list
    // must be sorted before it is assigned (the backend order is not guaranteed).
    // Same-day samples are all kept (not deduplicated); rows without a usable
    // start date are dropped so a missing date cannot land at epoch 0 (1970) and
    // stretch the x-axis back fifty years, squashing every real point.
    prepareSampleChartData(data: RiskRegisterSample[]): SampleChartRow[] {
        return (data ?? [])
            .filter(
                (res): res is RiskRegisterSample & { startDate: number } =>
                    typeof res.startDate === "number" &&
                    isFinite(res.startDate) &&
                    res.startDate > 0,
            )
            .map((res) => {
                const isNonDetect =
                    res.sampleCensorType === SampleCensorType.LessThan;
                const isExcluded = isExcludedFromStatistics(
                    res.sampleCensorType,
                    res.substitutedValue,
                );
                const pointState: SampleChartPointState = !isNonDetect
                    ? "detected"
                    : isExcluded
                      ? "excluded"
                      : "substituted";
                const reportedNumber =
                    typeof res.value === "number"
                        ? res.value
                        : parseFloat(res.value as string);
                // Excluded samples have no substituted value; plot them at their
                // reported level (greyed) so the user still sees where they sit.
                const plottedValue = isExcluded
                    ? reportedNumber
                    : (res.substitutedValue ?? reportedNumber);
                const unitSuffix = res.unit ? ` ${res.unit}` : "";
                const reportedDisplay = this.formatReportedDisplay(res);
                const substitutedDisplay = isExcluded
                    ? "excluded from statistics"
                    : res.substitutedValue != null
                      ? `${res.substitutedValue}${unitSuffix}`
                      : "-";
                return {
                    ...res,
                    sampleId: res.sampleId.toString(),
                    startDate: res.startDate * 1000,
                    plottedValue,
                    pointState,
                    reportedDisplay,
                    substitutedDisplay,
                };
            })
            .sort((a, b) => a.startDate - b.startDate);
    }

    // Computes padded y-axis bounds across the plotted sample values and every
    // reference line (OEL / decision statistic / geometric mean) so the axis
    // range covers them all and no bullet is clipped at an edge. When every
    // value is identical the span is zero, so an absolute fallback pad is used
    // to keep the points off the axis floor.
    computeSampleChartYAxisBounds(
        rows: Array<RiskRegisterSample & { plottedValue?: number }>,
    ): { min: number; max: number } | null {
        const values = (rows ?? [])
            .flatMap((row) => [
                row.plottedValue ?? row.value,
                row.valueOel,
                row.valueQuantitativeDecisionStatisticValue,
                row.valueQuantitativeGeometricMean,
            ])
            .filter(
                (value): value is number =>
                    typeof value === "number" && isFinite(value),
            );
        if (!values.length) {
            return null;
        }
        const dataMin = Math.min(...values);
        const dataMax = Math.max(...values);
        const span = dataMax - dataMin;
        const pad =
            span > 0
                ? span *
                  RiskRegisterDetailsPopupComponent.SAMPLE_CHART_AXIS_PAD_RATIO
                : Math.max(
                      Math.abs(dataMin) *
                          RiskRegisterDetailsPopupComponent.SAMPLE_CHART_AXIS_PAD_RATIO,
                      RiskRegisterDetailsPopupComponent.SAMPLE_CHART_MIN_AXIS_PAD,
                  );
        return { min: dataMin - pad, max: dataMax + pad };
    }

    async updateChartData(data: RiskRegisterSample[]) {
        const { am5: am5Ns, am5xy: am5xyNs } = await this.chartService.getAmCharts5();
        const chart = this.createXYChart(am5Ns, am5xyNs);
        const root = this.chartRoot!;
        this.showSampleMarkerLegend = true;
        const unit = data.find((res) => res.unit)?.unit;
        const rows = this.prepareSampleChartData(data);

        await this.setChartMenu();

        const xAxis = chart.xAxes.push(am5xyNs.DateAxis.new(root, {
            baseInterval: { ...RiskRegisterDetailsPopupComponent.SAMPLE_CHART_BASE_INTERVAL },
            extraMin: 0.1,
            extraMax: 0.1,
            renderer: am5xyNs.AxisRendererX.new(root, {}),
            tooltipDateFormats: { millisecond: this.dateFormatService.userDateFormat },
            tooltip: am5Ns.Tooltip.new(root, {}),
        }));

        const bounds = this.computeSampleChartYAxisBounds(rows);
        const yAxis = chart.yAxes.push(am5xyNs.ValueAxis.new(root, {
            ...(bounds ? { min: bounds.min, max: bounds.max, strictMinMax: true } : {}),
            renderer: am5xyNs.AxisRendererY.new(root, {}),
        }));
        const yAxisLabel = am5Ns.Label.new(root, {
            rotation: -90,
            text: unit,
            y: am5Ns.p50,
            centerX: am5Ns.p50,
        });
        yAxis.children.unshift(yAxisLabel);
        // The padded range can dip below zero; rendered labels there are noise.
        yAxis.get("renderer").labels.template.adapters.add("text", (text) => {
            return text != null && parseFloat(text) < 0 ? "" : text;
        });

        const series = chart.series.push(am5xyNs.LineSeries.new(root, {
            name: this.translateService.getDynamicTranslation("Line Series"),
            xAxis,
            yAxis,
            valueXField: "startDate",
            // Plot the substituted value so the points and the OEL / decision-statistic
            // / geometric-mean reference lines share one basis (what the statistics used).
            valueYField: "plottedValue",
            tooltip: am5Ns.Tooltip.new(root, {
                labelText: this.buildSampleTooltipText(this.resolveCensoredDataMethodLabel()),
            }),
        }));
        series.strokes.template.set("strokeWidth", 0);

        const oelSeries = chart.series.push(am5xyNs.LineSeries.new(root, {
            name: "OEL",
            xAxis,
            yAxis,
            valueXField: "startDate",
            valueYField: "valueOel",
        }));
        oelSeries.strokes.template.set("strokeWidth", 2);

        const decisionStatisticSeries = chart.series.push(am5xyNs.LineSeries.new(root, {
            name: this.translateService.getDynamicTranslation("Decision Statistic Value"),
            xAxis,
            yAxis,
            valueXField: "startDate",
            valueYField: "valueQuantitativeDecisionStatisticValue",
        }));
        decisionStatisticSeries.strokes.template.set("strokeWidth", 2);

        const geometricMeanSeries = chart.series.push(am5xyNs.LineSeries.new(root, {
            name: this.translateService.getDynamicTranslation("Quantitative Geometric Mean"),
            xAxis,
            yAxis,
            valueXField: "startDate",
            valueYField: "valueQuantitativeGeometricMean",
        }));
        geometricMeanSeries.strokes.template.set("strokeWidth", 2);

        chart.children.push(am5Ns.Legend.new(root, {}));
        const updateCursor = am5xyNs.XYCursor.new(root, { xAxis, yAxis, behavior: "zoomX" });
        updateCursor.set("snapToSeries", [series]);
        chart.set("cursor", updateCursor);

        this.styleSampleBullets(series, am5Ns, root);

        const actionHint = this.translateService.getDynamicTranslation("Click to mark as outlier");
        const canMark = this.canMarkOutliers;
        const chartRows = rows.map((row) => ({
            ...row,
            actionHint: canMark && row.sampleId && row.sampleNumber ? actionHint : "",
        }));
        [series, oelSeries, decisionStatisticSeries, geometricMeanSeries].forEach((s) =>
            s.data.setAll(chartRows),
        );
    }

    // Per-point bullet styling for the sample chart, keyed on each row's pointState.
    // Detected points are filled in the series colour; non-detects are open circles in
    // muted greys, and both non-detect states also carry a short downward tick - the true
    // value lies at or below the plotted point, which colour alone cannot express.
    // All three states stay distinguishable in greyscale (filled vs open, plus the
    // tick), satisfying the colour-blind requirement.
    private styleSampleBullets(
        series: am5xy.LineSeries,
        am5Ns: typeof import("@amcharts/amcharts5"),
        root: am5.Root,
    ): void {
        const detected = am5Ns.color(
            this.readCssColor(
                "--primary",
                RiskRegisterDetailsPopupComponent.SAMPLE_POINT_DETECTED_COLOR,
            ),
        );
        const substituted = am5Ns.color(
            this.readCssColor(
                "--neutral-500",
                RiskRegisterDetailsPopupComponent.SAMPLE_POINT_SUBSTITUTED_COLOR,
            ),
        );
        const excluded = am5Ns.color(
            this.readCssColor(
                "--neutral-400",
                RiskRegisterDetailsPopupComponent.SAMPLE_POINT_EXCLUDED_COLOR,
            ),
        );
        const colorFor = (state: SampleChartPointState) =>
            state === "detected"
                ? detected
                : state === "excluded"
                  ? excluded
                  : substituted;
        // v4 read the row off target.dataItem.dataContext inside property adapters;
        // a v5 bullet factory is handed the dataItem directly, so the per-point state
        // is resolved once when the bullet is built instead of on every redraw.
        const stateOf = (dataItem: { dataContext?: unknown }): SampleChartPointState =>
            (dataItem?.dataContext as SampleChartRow | undefined)?.pointState ?? "detected";

        series.bullets.push((_root, _series, dataItem) => {
            const state = stateOf(dataItem);
            const color = colorFor(state);
            const row = dataItem?.dataContext as SampleChartRow | undefined;
            const circle = am5Ns.Circle.new(root, {
                radius: 4,
                strokeWidth: 2,
                stroke: color,
                fill: color,
                // Open (unfilled) for non-detects, filled for detected - the conventional
                // statistical notation for censored vs measured.
                fillOpacity: state === "detected" ? 1 : 0,
                focusable: true,
                ariaLabel: row ? this.buildSampleAriaLabel(row) : undefined,
            });
            if (row?.actionHint && row.sampleId) {
                this.makePointMarkable(circle, row.sampleId);
            }
            return am5Ns.Bullet.new(root, { sprite: circle });
        });

        // Short downward tick, shown on both non-detect states (substituted and
        // excluded): both are censored observations, so the true value lies at or
        // below the plotted point. Coloured per state to match the marker. Returning
        // undefined is the v5 equivalent of v4's `disabled` adapter - no bullet is
        // created for a detected point.
        series.bullets.push((_root, _series, dataItem) => {
            const state = stateOf(dataItem);
            if (!this.sampleMarkerHasTick(state)) {
                return undefined;
            }
            return am5Ns.Bullet.new(root, {
                sprite: am5Ns.Line.new(root, {
                    points: [
                        { x: 0, y: 0 },
                        { x: 0, y: 9 },
                    ],
                    strokeWidth: 2,
                    stroke: colorFor(state),
                }),
            });
        });
    }

    // amCharts runs a focusable sprite's click handler on Enter / Space, so the same handler serves mouse and keyboard.
    private makePointMarkable(sprite: am5.Sprite, sampleId: string): void {
        sprite.set("cursorOverStyle", "pointer");
        sprite.events.on("click", () => this.markChartSampleAsOutlier(sampleId));
    }

    private buildSampleAriaLabel(row: SampleChartRow): string {
        return [
            `${this.translateService.getDynamicTranslation("Sample Number")}: ${row.sampleNumber ?? "-"}`,
            `${this.translateService.getDynamicTranslation("Date Collected")}: ${this.dateFormatService.formatTimestampToString(row.startDate, this.dateFormatService.userDateFormat)}`,
            `${this.translateService.getDynamicTranslation("Reported")}: ${row.reportedDisplay}`,
            ...(row.pointState !== "detected" ? [this.translateService.getDynamicTranslation("Non-detect")] : []),
            ...(row.actionHint ? [this.translateService.getDynamicTranslation("Press Enter to mark as outlier")] : []),
        ].join(", ");
    }

    // Resolves the live value of a CSS custom property (design token), falling back
    // to a hard-coded hex when it cannot be read (e.g. under headless unit tests).
    private readCssColor(name: string, fallback: string): string {
        try {
            const value = getComputedStyle(document.documentElement)
                .getPropertyValue(name)
                .trim();
            return value || fallback;
        } catch {
            return fallback;
        }
    }

    // Display label for the CensoredDataMethod snapshotted on the register, shown in
    // the sample tooltip so the user knows the basis the plotted point sits on.
    private resolveCensoredDataMethodLabel(): string {
        const method = this.riskDetails?.censoredDataMethod;
        return method != null
            ? (CENSORED_DATA_METHOD_DISPLAY_LABELS[method as CensoredDataMethod] ??
                  CENSORED_DATA_METHOD_DISPLAY_LABELS[
                      CensoredDataMethod.LodOverSqrt2
                  ])
            : CENSORED_DATA_METHOD_DISPLAY_LABELS[CensoredDataMethod.LodOverSqrt2];
    }

    // Builds the AmCharts tooltip template for sample-result data points. The
    // tooltip surfaces the user-facing Sample Number (not the internal Sample ID),
    // and shows BOTH the plotted (substituted) value used by the statistics and the
    // reported value with its censor sign, plus the censored-data method in force —
    // a non-detect is a censored observation and neither number alone tells the
    // whole story. {reportedDisplay} and {substitutedDisplay} come from the prepared
    // row (per point); the method label is register-level so it is baked in here.
    private buildSampleTooltipText(methodLabel: string): string {
        const sampleNumberLabel =
            this.translateService.getDynamicTranslation("Sample Number");
        const dateCollectedLabel =
            this.translateService.getDynamicTranslation("Date Collected");
        const plottedLabel = this.translateService.getDynamicTranslation(
            "Plotted (used in statistics)",
        );
        const reportedLabel =
            this.translateService.getDynamicTranslation("Reported");
        const methodLabelText = this.translateService.getDynamicTranslation(
            "Censored-data method",
        );

        // v4 resolved {dateX}/{value} as the series' bound date/value axis fields;
        // v5's equivalent tokens are {valueX}/{valueY}. {sampleNumber} and {unit} are
        // plain data-context fields, unchanged between versions.
        return `${sampleNumberLabel}: [bold]{sampleNumber}[/]
                          ${dateCollectedLabel}: [bold]{valueX.formatDate('${this.dateFormatService.userDateFormat}')}[/]
                          ${plottedLabel}: [bold]{plottedValue} {unit}[/]
                          ${reportedLabel}: [bold]{reportedDisplay}[/]
                          ${methodLabelText}: [bold]${methodLabel}[/]
                          {actionHint}`;
    }

    // OLS regression of empirical on theoretical quantiles, surfaced as a
    // numeric goodness-of-fit readout (slope → 1, R² → 1 == good log-normal fit).
    // This is a readout only; the chart reference line stays the y = x identity.
    computeQqGoodnessOfFit(qqplot?: QqPlot): void {
        this.qqFitSlope = null;
        this.qqFitR2 = null;
        const theoretical = qqplot?.theoreticalQuantiles ?? [];
        const empirical = qqplot?.empiricalQuantiles ?? [];
        const n = Math.min(theoretical.length, empirical.length);
        if (n < 2) {
            return;
        }

        const meanX =
            theoretical.slice(0, n).reduce((sum, x) => sum + x, 0) / n;
        const meanY = empirical.slice(0, n).reduce((sum, y) => sum + y, 0) / n;

        let sxx = 0;
        let syy = 0;
        let sxy = 0;
        for (let i = 0; i < n; i++) {
            const dx = theoretical[i] - meanX;
            const dy = empirical[i] - meanY;
            sxx += dx * dx;
            syy += dy * dy;
            sxy += dx * dy;
        }

        if (sxx === 0 || syy === 0) {
            return;
        }

        this.qqFitSlope = sxy / sxx;
        this.qqFitR2 = (sxy * sxy) / (sxx * syy);
    }

    async createScatterPlot(qqplot: QqPlot) {
        const { am5: am5Ns, am5xy: am5xyNs } = await this.chartService.getAmCharts5();
        const chart = this.createXYChart(am5Ns, am5xyNs);
        const root = this.chartRoot!;
        this.showSampleMarkerLegend = false;

        // Both quantile sets are sample values stored in the base unit (µg/m³);
        // convert them to the register's agent unit so the plot and its tooltip
        // read in the same unit as the statistics panel. Both axes share the one
        // factor, so the fit, identity line and R² are unchanged.
        const data = this.buildQqChartRows(qqplot);
        const theoretical = data.map((row) => row.theoretical);
        const empirical = data.map((row) => row.empirical);

        await this.setChartMenu();

        const unitSuffix = this.agentUnitSuffix();
        const xAxis = chart.xAxes.push(am5xyNs.ValueAxis.new(root, {
            renderer: am5xyNs.AxisRendererX.new(root, {}),
        }));
        const xAxisLabel = am5Ns.Label.new(root, {
            text: this.translateService.getDynamicTranslation("Theoretical Quantiles") + unitSuffix,
            x: am5Ns.p50,
            centerX: am5Ns.p50,
        });
        xAxis.children.push(xAxisLabel);

        const yAxis = chart.yAxes.push(am5xyNs.ValueAxis.new(root, {
            renderer: am5xyNs.AxisRendererY.new(root, {}),
        }));
        const yAxisLabel = am5Ns.Label.new(root, {
            rotation: -90,
            text: this.translateService.getDynamicTranslation("Empirical Quantiles") + unitSuffix,
            y: am5Ns.p50,
            centerX: am5Ns.p50,
        });
        yAxis.children.unshift(yAxisLabel);

        // amCharts5 has no dedicated scatter series type — a LineSeries with its
        // stroke hidden and only bullets visible reproduces v4's bare XYSeries
        // scatter (mirrors the band-series idiom in node-analytics-chart.component.ts,
        // which likewise hides strokes selectively to show only fills/bullets).
        const series = chart.series.push(am5xyNs.LineSeries.new(root, {
            name: this.translateService.getDynamicTranslation("QQ Plot"),
            xAxis,
            yAxis,
            valueXField: "theoretical",
            valueYField: "empirical",
        }));
        series.strokes.template.set("strokeOpacity", 0);

        const theoreticalValues = theoretical.filter(
            (value): value is number => value != null,
        );
        const empiricalValues = empirical.filter(
            (value): value is number => value != null,
        );
        const minTheoretical = Math.min(...theoreticalValues);
        const maxTheoretical = Math.max(...theoreticalValues);
        const minEmpirical = Math.min(...empiricalValues);
        const maxEmpirical = Math.max(...empiricalValues);

        // y = x identity reference line across the combined range. The theoretical
        // axis is built from the same fitted log-normal μ/σ as the data, so a
        // perfect fit means empirical = theoretical; goodness of fit is read from
        // how closely the points hug this fixed 45° line (it does NOT tilt to
        // chase the extreme samples like the old min/max chord did).
        const lineMin = Math.min(minTheoretical, minEmpirical);
        const lineMax = Math.max(maxTheoretical, maxEmpirical);
        const lineSeries = chart.series.push(am5xyNs.LineSeries.new(root, {
            name: this.translateService.getDynamicTranslation("Identity Line (y = x)"),
            xAxis,
            yAxis,
            valueXField: "theoretical",
            valueYField: "empirical",
            stroke: am5Ns.color("#0000FF"),
        }));
        lineSeries.strokes.template.setAll({ strokeWidth: 2, strokeDasharray: [5, 5] });
        lineSeries.data.setAll([
            { theoretical: lineMin, empirical: lineMin },
            { theoretical: lineMax, empirical: lineMax },
        ]);

        // The tooltip sits on each point rather than the series, so only sample points show sample details and the
        // y = x reference line shows none.
        series.bullets.push((_root, _series, dataItem) => {
            const row = dataItem?.dataContext as QqChartRow | undefined;
            const circle = am5Ns.Circle.new(root, {
                radius: 4,
                fill: am5Ns.color("#FF0000"),
                strokeWidth: 2,
                focusable: true,
                tooltipText: row?.tooltip,
                tooltip: am5Ns.Tooltip.new(root, {}),
                ariaLabel: row?.ariaLabel,
            });
            if (row?.sampleId) {
                this.makePointMarkable(circle, row.sampleId);
            }
            return am5Ns.Bullet.new(root, { sprite: circle });
        });

        chart.set("cursor", am5xyNs.XYCursor.new(root, { xAxis, yAxis, behavior: "zoomX" }));

        const legend = chart.children.push(am5Ns.Legend.new(root, {}));
        legend.data.setAll(chart.series.values);

        series.data.setAll(data);
    }

    async createLinePlot(pdfPlot: PdfPlot) {
        const { am5: am5Ns, am5xy: am5xyNs } = await this.chartService.getAmCharts5();
        const chart = this.createXYChart(am5Ns, am5xyNs);
        const root = this.chartRoot!;
        this.showSampleMarkerLegend = false;
        const colors = this.commonService.colors;

        // The curve x values and every reference value arrive in the base unit
        // (µg/m³); convert them to the register's agent unit so the axis, labelled
        // with that unit, matches the statistics panel below the chart.
        const oel = this.toDisplayUnit(
            this.oelReferenceLineValue(this.riskDetails),
        );
        const gm = this.toDisplayUnit(this.riskDetails.quantitativeGeometricMean);
        const dsv = this.toDisplayUnit(
            this.riskDetails.quantitativeDecisionStatisticValue,
        );

        const data = pdfPlot.xValues.map((xValue, index) => ({
            x: this.toDisplayUnit(xValue),
            pdf: pdfPlot.pdfValues[index],
            oel,
            gm,
            dsv,
        }));

        await this.setChartMenu();

        // A log-normal density collapses to a spike on a linear axis for any large
        // GSD, so the linear-dose path uses a logarithmic x-axis (the backend
        // returns geometrically spaced x values so they render evenly). Noise
        // arrives already in dB - log space - and stays on a linear axis; a second
        // log transform would be wrong.
        const logarithmic = this.isLogarithmicDoseAxis();
        const xAxis = chart.xAxes.push(am5xyNs.ValueAxis.new(root, {
            logarithmic,
            renderer: am5xyNs.AxisRendererX.new(root, {}),
        }));
        const xAxisLabel = am5Ns.Label.new(root, {
            text: this.riskDetails.agentUnits != null ? UnitSymbols[this.riskDetails.agentUnits] : "",
            x: am5Ns.p50,
            centerX: am5Ns.p50,
        });
        xAxis.children.push(xAxisLabel);

        const yAxis = chart.yAxes.push(am5xyNs.ValueAxis.new(root, {
            renderer: am5xyNs.AxisRendererY.new(root, {}),
        }));
        const yAxisLabel = am5Ns.Label.new(root, {
            rotation: -90,
            text: this.translateService.getDynamicTranslation("Probability Distribution Plot"),
            y: am5Ns.p50,
            centerX: am5Ns.p50,
        });
        yAxis.children.unshift(yAxisLabel);

        const series = chart.series.push(am5xyNs.LineSeries.new(root, {
            name: this.translateService.getDynamicTranslation("PDF Plot"),
            xAxis,
            yAxis,
            valueXField: "x",
            valueYField: "pdf",
            tooltip: am5Ns.Tooltip.new(root, { labelText: "{valueX}: {valueY}" }),
        }));
        series.strokes.template.set("strokeWidth", 2);

        // A log axis cannot represent zero or negative values, so a non-positive
        // reference is skipped explicitly here rather than being pushed as a series
        // the chart would silently drop.
        const referenceSeries: am5xy.LineSeries[] = [];
        const addReferenceLine = (
            field: "oel" | "gm" | "dsv",
            value: number | null,
            color: string,
            name: string,
        ): void => {
            if (!this.canPlotReference(value, logarithmic)) {
                return;
            }
            const line = chart.series.push(am5xyNs.LineSeries.new(root, {
                name,
                xAxis,
                yAxis,
                valueXField: field,
                valueYField: "pdf",
                stroke: am5Ns.color(color),
            }));
            line.strokes.template.set("strokeDasharray", [5, 5]);
            referenceSeries.push(line);
        };

        addReferenceLine("oel", oel, colors.red, "OEL");
        addReferenceLine(
            "gm",
            gm,
            colors.green,
            this.translateService.getDynamicTranslation("Geometric Mean"),
        );
        addReferenceLine(
            "dsv",
            dsv,
            colors.lightPurple,
            this.translateService.getDynamicTranslation("Decision Statistic Value"),
        );

        const legend = chart.children.push(am5Ns.Legend.new(root, {}));
        legend.data.setAll(chart.series.values);

        const pdfCursor = am5xyNs.XYCursor.new(root, { xAxis, yAxis, behavior: "zoomX" });
        pdfCursor.set("snapToSeries", [series]);
        chart.set("cursor", pdfCursor);

        [series, ...referenceSeries].forEach((s) => s.data.setAll(data));
    }

    // Converts a base-unit (µg/m³) value to the register's agent unit for display,
    // mirroring the statistics rows. Null/undefined is preserved so an absent
    // reference is not coerced to zero and plotted.
    private toDisplayUnit(value: number | null | undefined): number | null {
        if (value == null) {
            return null;
        }
        const unit = this.riskDetails?.agentUnits;
        return unit == null
            ? value
            : this.valueConversionService.getParameterValueAsPerAgentUnit(
                  value,
                  unit,
              );
    }

    // The backend reports a noise register's statistics in the shift-average (dB)
    // frame, so its OEL reference line must be the shift-adjusted CorrectedOEL to sit
    // in the same frame as the sample points; chemicals keep the base OEL.
    private oelReferenceLineValue(register: RiskRegister): number {
        return register.agentUnits === Units.Db
            ? (register.correctedOEL ?? register.oel)
            : register.oel;
    }

    // Noise registers are back-converted to dB (log space) by the backend, so they
    // stay on a linear axis; every other agent unit is a linear dose that only reads
    // correctly on a logarithmic axis.
    private isLogarithmicDoseAxis(): boolean {
        return this.riskDetails?.agentUnits !== Units.Db;
    }

    private canPlotReference(
        value: number | null,
        logarithmic: boolean,
    ): boolean {
        if (value == null) {
            return false;
        }
        return !logarithmic || value > 0;
    }

    private agentUnitSuffix(): string {
        const unit = this.riskDetails?.agentUnits;
        return unit != null ? ` (${UnitSymbols[unit]})` : "";
    }


    /**
     * v4's exporting.menu / ExportMenu becomes the v5 exporting plugin: an
     * `Exporting` instance (the export engine) plus an `ExportingMenu` (the "..."
     * trigger button) attached to the current chart's root. PNG and JPG are
     * preserved 1:1. SVG has no v5 exporting format (v5's ExportingFormats union
     * is png/jpg/canvas/pdf/xlsx/csv/json/html/pdfdata/print — no "svg"), so the
     * SVG menu entry is dropped rather than faked; flagged for manual follow-up
     * if SVG export is actually relied on downstream.
     */
    async setChartMenu() {
        const { am5plugins_exporting: exportingNs } = await this.chartService.getAmCharts5();
        const root = this.chartRoot!;
        const exporting = exportingNs.Exporting.new(root, {
            menu: exportingNs.ExportingMenu.new(root, {
                items: [
                    { type: "format", format: "png", label: "PNG" },
                    { type: "format", format: "jpg", label: "JPG" },
                ],
            }),
        });
        exporting.events.on("exportstarted", () => {
            this.isInteractionPaused = true;
        });
        exporting.events.on("exportfinished", () => {
            document.addEventListener(
                "click",
                () => {
                    this.isInteractionPaused = false;
                },
                { once: true },
            );
        });
    }

    /**
     * Creates a fresh am5.Root + am5xy.XYChart bound to CHART_DIV_ID, disposing any
     * previously built root first. The prior amCharts major version reused a single
     * chart instance across renders (re-creating against the same div re-initialized
     * it in place); amCharts5 has no such re-init — the whole Root (and everything
     * under it, including the exporting plugin's menu DOM) must be disposed and
     * rebuilt per chart.
     */
    private createXYChart(
        am5Ns: typeof import("@amcharts/amcharts5"),
        am5xyNs: typeof import("@amcharts/amcharts5/xy"),
    ): am5xy.XYChart {
        this.chartRoot?.dispose();
        const root = am5Ns.Root.new(this.CHART_DIV_ID);
        root.setThemes([]);
        root._logo?.dispose();
        this.chartRoot = root;
        this.chart = root.container.children.push(am5xyNs.XYChart.new(root, { layout: root.verticalLayout }));
        return this.chart;
    }

    pickerEnabled(isEnabled = true) {
        this.isInteractionPaused = isEnabled;
    }

    /**
     * Info icons show their popover on hover (CSS). Clicking an icon pins it open until the
     * next click outside; clicking the same icon again unpins it. stopPropagation keeps the
     * document click handler (which unpins) from firing for this same click.
     */
    toggleInfoPopover(id: string, event: MouseEvent) {
        event.stopPropagation();
        this.pinnedInfoPopover = this.pinnedInfoPopover === id ? null : id;
    }

    @HostListener("document:click")
    onDocumentClick() {
        if (this.pinnedInfoPopover) {
            this.pinnedInfoPopover = null;
        }
    }

    toggleDateChange(enable = true) {
        this.editDateRange = enable;
        if (!enable) {
            this.riskDetails.periodStart = this.initialRiskDetails.periodStart;
            this.riskDetails.periodEnd = this.initialRiskDetails.periodEnd;
            this.startDate = this.dateFormatService.convertTimestampToDate(
                this.riskDetails.periodStart,
            );
            this.endDate = this.dateFormatService.convertTimestampToDate(
                this.riskDetails.periodEnd,
            );
        }
    }

    saveDateChange() {
        this.editDateRange = false;
        this.riskDetails = {
            ...this.initialRiskDetails,
            periodStart: this.riskDetails.periodStart,
            periodEnd: this.riskDetails.periodEnd,
        };
        this.save();
    }

    toggleOelCorrectionExpanded() {
        this.oelCorrectionExpanded = !this.oelCorrectionExpanded;
    }

    onTabChange(event: MatTabChangeEvent) {
        const selectedSection = this.filteredOelSections[event.index];
        this.chartRoot?.dispose();
        this.chartRoot = null;
        switch (selectedSection) {
            case "Goodness of fit":
                this.createScatterPlot(this.riskDetails.qqPlot);
                break;
            case "Log-normal fit":
                this.createLinePlot(this.riskDetails.pdfPlot);
                break;
            default:
                this.updateChartData(this.riskDetails.riskRegisterSamples);
                break;
        }
    }

    updateRiskTableData(event?: MatTabChangeEvent) {
        if (event?.tab) this.selectedRiskTab = event.index;
        this.refreshAttachmentState();
        if (this.selectedRiskTab === 1) {
            this.dataSource.data = [];
            this.scheduleBayesianChartRender();
            return;
        }

        this.disposeBayesianCharts();

        const treatmentTabIndex = 2;
        const isAssessmentTab = this.selectedRiskTab === 0;
        const isTreatmentTab = this.selectedRiskTab === treatmentTabIndex;

        this.controlTreatmentField.readonly = this.isApproved;
        this.controlAssessmentField.readonly = this.isApproved;

        this.controlTreatmentField.options = this.controls.filter(
            (res) =>
                !this.riskDetails.riskAssessmentControlIds.includes(
                    res.id as string,
                ),
        );

        this.controlAssessmentField.options = this.controls.filter(
            (res) =>
                !this.riskDetails.riskTreatmentControlIds.includes(
                    res.id as string,
                ),
        );

        this.dataSource.data = (
            this.riskDetails.riskRegisterControls as Control[]
        ).filter((res) =>
            isAssessmentTab
                ? this.riskDetails.riskAssessmentControlIds.includes(res.id)
                : isTreatmentTab
                  ? this.riskDetails.riskTreatmentControlIds.includes(res.id)
                  : false,
        );
    }

    private buildMatrixData(): void {
        // DASH-2375: build from jurisdiction likelihood levels so the matrix matches
        // Risk Configuration; fall back to qualitative mapping when no config exists.
        this.matrixRows =
            this.likelihoodLevels.length > 0
                ? this.buildMatrixRowsFromLevels()
                : this.buildMatrixRowsFromMapping();
        this.matrixColumns = [...(this.consequenceRatings || [])].sort(
            (a, b) => a.value - b.value,
        );
        this.matrixCells = this.matrixRows.map(row =>
            this.matrixColumns.map(col => ({
                color: this.getMatrixCellColor(row.score, col.value),
                isActive: this.isActiveCell(row.score, col.value),
                tooltip: this.getCellTooltip(row.score, col.value)
            }))
        );
    }

    private buildMatrixRowsFromLevels(): Array<{ score: number; classification: string }> {
        return this.likelihoodLevels
            .map(level => ({
                score: level.score,
                classification: level.name
                    ? `${level.score} ${level.name}`
                    : String(level.score),
            }))
            .sort((a, b) => b.score - a.score);
    }

    private buildMatrixRowsFromMapping(): Array<{ score: number; classification: string }> {
        const uniqueScores = Array.from(
            new Set(this.likelihoodMapping.map(m => m.likelihoodScore))
        ).sort((a, b) => b - a);

        return uniqueScores.map(score => {
            const mapping = this.likelihoodMapping.find(m => m.likelihoodScore === score);
            return {
                score,
                classification: mapping?.factor || mapping?.classification || String(score),
            };
        });
    }

    getMatrixCellColor(likelihood: number, consequence: number): string {
        const riskValue = likelihood * consequence;
        return getRiskColor(riskValue, this.riskLevels);
    }

    isActiveCell(likelihood: number, consequence: number): boolean {
        if (!this.riskDetails) return false;

        const currentLikelihood =
            this.riskDetails.methodOfAssessment ===
            AssessmentMethodEnum.Quantitative
                ? this.riskDetails.quantitativeLikelihoodScore
                : this.riskDetails.methodOfAssessment ===
                    AssessmentMethodEnum.Qualitative
                  ? this.riskDetails.qualitativeLikelihoodScore
                  : 0;

        const currentConsequence = this.riskDetails.consequenceRating || 0;
        return likelihood === currentLikelihood && consequence === currentConsequence;
    }

    getConsequenceLabel(consequence: ConsequenceRating): string {
        return `${consequence.value} - ${consequence.label}`;
    }

    getCellTooltip(likelihood: number, consequence: number): string {
        const score = likelihood * consequence;
        const prefix = this.translateService.getDynamicTranslation('Risk: ');
        return `${prefix}${score} (${likelihood} × ${consequence})`;
    }

    get consequenceRatingDescription(): string {
        if (!this.riskDetails?.consequenceRating) return '';
        const consequence = this.consequenceRatings.find(c => c.value === this.riskDetails.consequenceRating);
        return consequence?.description || '';
    }

    get residualExposureDescription(): string {
        if (!this.riskDetails?.residualExposureScore || this.riskDetails.residualExposureScore <= 0) {
            return '';
        }
        const option = this.exceedanceProbabilityList.find(x => x.value === this.riskDetails.residualExposureScore);
        return option?.description || '';
    }

    get residualFrequencyDescription(): string {
        if (!this.riskDetails?.residualFrequencyScore || this.riskDetails.residualFrequencyScore <= 0) {
            return '';
        }
        const option = this.frequencyExposureList.find(x => x.value === this.riskDetails.residualFrequencyScore);
        return option?.description || '';
    }

    public trackByOptionValue = (
        index: number,
        option: Pick<QualitativeConfigOption, 'value'>,
    ): number => option.value ?? index;

    public trackByIndex = trackByIndex;

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        super.ngOnDestroy();
        this.disposeBayesianCharts();
        this.chartRoot?.dispose();
        this.chartRoot = null;
    }
}
